// =============================================================================
// 05102026 -- BASELINE: non-EEA consent fast path on both Puls variants (no early auction)
// =============================================================================
//
// Base: 01102026-2/ads-a.js (the "fast path alone" arm A of the speed A/B).
//
// Why this build (05.10.2026): on 05.10.2026 both CMS variants (PRODUCTION and
// PRODUCTION_B) were moved to the fast-path-alone file after the early
// first-screen auction arm (01102026/ads-b) had read null on speed (pages with
// the first ad request at >= 2 s only -3 points over three clean days) and
// negative on the first-screen Prebid money (Prebid EUR per 1,000 served on the
// first-screen units -9 % to -16 % below the fast-path-alone arm). The early
// auction is retired; the fast path is the baseline for every page view.
//
// What this file changes against 01102026-2 (three things, nothing else):
//
//   1. The country stamp is read ONLY under window.adsVisitorCountry. The head
//      template was renamed to that name on 01.10.2026; the fallback to the
//      former spelling existed only so the template change and the wrapper
//      could land in either order. See CONSENT_FAST_PATH_COUNTRY_GLOBALS.
//
//   2. userSync.auctionDelay 50 -> 300 ms: the first Prebid auction waits for the
//      user-id submodules to resolve, capped at 300 ms. See the comment block at
//      the userSync configuration for the reasoning and the measurement behind it.
//
//   3. Every Adform ('adf') bid entry carries priceType: 'net', like the
//      mediapool alias entries already did. See the comment at the first entry.
//
// Telemetry values stay BARE (aq_first_request = lt_500 ... 5000_plus, no arm
// prefix). The async-safe boot guard at the end of the file is kept: both CMS
// variants load this file `async`.
//
// Still open for a later build (not in this one): the page-level
// googletag.pubads().setTargeting() calls -> googletag.setConfig({ targeting }),
// and userSync.topics -> topics: false together with dropping topicsFpdModule
// from the Prebid library build.
//
// Kill switch: CONSENT_FAST_PATH_ENABLED = false (rebuild) or repoint both CMS
// variants to 01102026-2/ads-a.min.js. Debugging: window.__pulsConsentFastPath.
// =============================================================================
//
// =============================================================================
// 01102026-2 -- ARM A of the Pulsonline speed A/B: NON-EEA CONSENT FAST PATH ALONE
//               (no early first-screen auction)
// =============================================================================
//
// Base: 25092026/ads.js (first-request timing telemetry, the file both CMS
// variants served until 28.09.2026; arm A = CMS PRODUCTION until this release).
// Arm B = CMS PRODUCTION_B, 01102026/ads-b.min.js = the same fast path STACKED on
// the 28092026 early first-screen auction (Billboard + P1 auctioned while the body
// parses, the rest of the ATF units auctioned at DOMContentLoaded).
//
// Why this build (01.10.2026, user decision after the day-1 read): the fast path
// is proven -- pages with the first ad request at >= 2 s dropped 8-9 points on
// arm B with the other countries (no fast path) unchanged and money flat -- so
// from now on BOTH arms answer the TCF question themselves for stamped non-EEA
// visitors at script execution. The only remaining difference between the arms
// is the early auction: arm B runs TWO first-screen Prebid auctions (Billboard +
// P1 early, the other ATF units at DOMContentLoaded), this arm runs the single
// ATF auction at DOMContentLoaded exactly as 25092026 did. The read now isolates
// what the early auction adds in speed and what its two-auction split costs on
// the first-screen Prebid money (arm B read -8.6% Prebid EUR/1k on those units
// on 01.10 while site-wide Prebid was flat).
//
// What this file adds to 25092026 (and nothing else):
//
//   1. The NON-EEA CONSENT FAST PATH section (ported unchanged from the arm B
//      file; the stamp is read under window.adsVisitorCountry only since the
//      05102026 build). installConsentFastPath() runs at script execution,
//      before the consent observer and before any requestBids call.
//   2. window.__pulsTimingSnapshot().consentFastPath (copy of the state).
//   3. An async-safe boot guard at the very end of the file: the PRODUCTION
//      template loads this file `async` from 01.10.2026 (it was `defer`), so the
//      file may execute after DOMContentLoaded already fired; it then boots at
//      once instead of waiting for an event that is not coming.
//
// Telemetry values stay BARE on this arm (aq_first_request = lt_500 ... 5000_plus,
// no prefix); arm B keeps its b_ prefix, so the existing per-arm read script
// (run_report_puls_early_auction_ab.py) is unchanged.
//
// Kill switch: CONSENT_FAST_PATH_ENABLED = false (rebuild) or repoint PRODUCTION
// to 25092026/ads.min.js (and untick async). Debugging: window.__pulsConsentFastPath.
// =============================================================================
//
// =============================================================================
// 25092026 -- FIRST-REQUEST TIMING TELEMETRY (single build for BOTH CMS variants)
// =============================================================================
//
// Base: 22092026/ads-b.js = the bid-cache baseline both CMS variants served since
// 24.09.2026 (the adaptive-timeout arm 24092026 was killed 25.09 after its first
// clean day: programmatic EUR/1k -0.9%, Prebid EUR/1k -1.2%). This release adds
// PASSIVE timing telemetry so the site's own "time to first ad request" waterfall
// can be read from GAM before any speed experiment is built. Nothing in it
// changes ad units, sizes, timeouts (fixed 1500 ms), refresh, lazy loading, the
// interstitial or the bid cache. Every telemetry function is wrapped in
// try/catch: a missing browser API can never break ad serving.
//
// What it adds (ported from the Zena.rs wrapper's 15092026 telemetry, same GAM
// keys, same bucket labels, so both sites read with the same script):
//
//   1. Two page-level GPT key-values, set right before the FIRST
//      googletag.pubads().refresh() call of the page view and therefore carried
//      by every ad request of that page view:
//
//        aq_first_request  -- milliseconds from the page's time origin
//                             (navigation start) to that first refresh() call,
//                             bucketed: lt_500, 500_999, 1000_1499, 1500_1999,
//                             2000_2999, 3000_4999, 5000_plus, unknown.
//
//        aq_consent_wait   -- milliseconds from the time origin to the moment
//                             consent became FINAL for this page: a real TCF
//                             "tcloaded" / "useractioncomplete" event, or a
//                             gdprApplies=false answer (the normal case for
//                             Serbian visitors). Same buckets; "pending" when
//                             the first ad request went out before consent was
//                             final; upgraded to the real bucket once consent
//                             becomes final, so the page's later requests (lazy
//                             slots, refreshes) carry the real number.
//
//      Both keys already exist in GAM (created 15.09.2026, network-wide,
//      predefined values, report-on): aq_first_request id 22769435,
//      aq_consent_wait id 22757868.
//
//   2. A consent observer: this wrapper has no CMP watchdog, so the telemetry
//      registers its own __tcfapi('addEventListener') callback and notes WHEN
//      consent became final and WHY. It never answers anything itself and never
//      changes how Prebid or GPT treat consent.
//
//   3. window.__pulsTimingSnapshot(): a debug snapshot with the raw milliseconds
//      behind the buckets, the wrapper's own milestones (script start, wrapper
//      start, first requestBids, first refresh) and the first GPT
//      slotRequested / slotResponseReceived / slotRenderEnded per slot.
//
// Read: the Zena first-request telemetry report logic on the Pulsonline units
// (CUSTOM_CRITERIA on the two keys x unit x device).
// =============================================================================

// =============================================================================
// 22092026 -- ARM B of the Pulsonline A/B "Prebid bid cache".
// Base: 11092026/ads-b.js (the file BOTH CMS variants load since 13.09.2026: wrapper-defined GPT web
// interstitial, promoted). ONE change: pbjs.setConfig({ useBidCache: true }) -- see the comment at the
// setConfig call. Arm A = CMS PROD, unchanged file 11092026/ads-b.min.js. Read by the theme-owned
// `variant` key: Prebid EUR per 1,000 served impressions, Prebid share of impressions, house share,
// AdX EUR per 1,000 (should stay flat or fall slightly as cached bids take impressions AdX used to
// backfill), per refresh_bucket (the cache can only help refresh auctions). Kill = repoint PROD_B to
// 11092026/ads-b.min.js.
// =============================================================================

// =============================================================================
// Release 26082026 -- Equativ bidder code corrected to "smartadserver".
//
// What changed, and only this:
//     The 12 Equativ bid entries previously carried the bidder code "equativ".
//     The Prebid library deployed alongside this wrapper compiles that adapter
//     under its older module name, "smartadserverBidAdapter", and does not
//     contain "equativBidAdapter" at all.
//
// Why it mattered:
//     Prebid silently discards a bid entry whose bidder code has no compiled
//     adapter. There is no console error, no bidderError event, and no
//     bidRequested event -- the entry simply never participates. Verified live
//     against production before making this change: across 355 bidder requests
//     over 6 page loads, "equativ" produced zero bid requests, while every
//     other configured bidder was called normally. Over the 22 days to
//     18.08.2026 the same integration earned effectively nothing in GAM, which
//     is what "never asked to bid" looks like in reporting.
//
// Why a rename rather than rebuilding the Prebid library:
//     Equativ is Smart AdServer's newer brand name. It is the same adapter and
//     it takes the same parameters -- networkId, siteId, pageId, formatId. The
//     placement IDs below are therefore unchanged, and they already match the
//     source-of-truth ID sheet. Correcting the bidder code is sufficient on its
//     own. Nothing else in this wrapper references a module that is missing
//     from the deployed library, so no library change is needed here.
//
// Reporting note:
//     Winning bids from this integration will now report in GAM as
//     hb_bidder=smartadserver, not hb_bidder=equativ. That value already exists
//     on the hb_bidder key, so no GAM key setup is required.
// =============================================================================

// Start the wrapper as early as possible for ATF slots (e.g., Billboard).
// Primary path: DOMContentLoaded -> run immediately.
document.addEventListener('DOMContentLoaded', function () {
    if (!window.prebidAuctionRun) {
        window.prebidAuctionRun = true;
        recordFirstRequestMilestone('wrapperStartMs', 'wrapperStartReason', 'DOMContentLoaded'); // telemetry (25092026)
        runWrapper();
    }
});

// Safety fallback in case DOMContentLoaded doesn't fire for any reason.
window.addEventListener('load', function () {
    if (!window.prebidAuctionRun) {
        window.prebidAuctionRun = true;
        recordFirstRequestMilestone('wrapperStartMs', 'wrapperStartReason', 'load'); // telemetry (25092026)
        runWrapper();
    }
});


// Reference to the global command queue for asynchronous execution of GPT-related calls.
var googletag = googletag || {};
googletag.cmd = googletag.cmd || [];

// Reference to the global command queue for asynchronous execution of Prebid-related calls.
var pbjs = pbjs || {};
pbjs.que = pbjs.que || [];

// -----------------------------------------------------------------------------
// 11092026 variant B -- GPT web interstitial defined by the wrapper
// -----------------------------------------------------------------------------
//
// What this release adds (and nothing else): one Google Publisher Tag out-of-page slot
// of format INTERSTITIAL on the existing GAM ad unit "/161634325/Pulsonline.rs/Interstitial".
// The page template does not carry an interstitial slot on Pulsonline, so the format was
// effectively absent on this site. A GPT interstitial needs no markup in the page: GPT
// creates its own full-screen container and shows the creative on the visitor's next
// navigation (link click) within the page, under Google's own frequency capping.
//
// How it is wired into this wrapper:
//   1. The slot is defined once per page, right after the div-driven slot definitions
//      and before enableServices(), on every device (desktop and mobile web).
//   2. It is NOT a Prebid ad unit: no header-bidding demand yet, so it is not added to
//      adUnitsATF / adUnitsBTF and takes no part in any pbjs.requestBids call. It is
//      filled by AdX / Open Bidding at the GAM pricing rule for interstitials.
//   3. It is requested together with the other above-the-fold slots in the first
//      googletag.pubads().refresh(initialSlots) call (single-request mode), i.e. after the
//      initial Prebid auction resolves, exactly like every other ATF slot.
//   4. It is never refreshed: the viewability-refresh setup skips out-of-page slots.
//      An out-of-page slot has no div of its own and its auto-generated element id
//      contains "/" characters, which would make document.querySelectorAll("#" + id)
//      throw; the skip protects the rest of the refresh loop as well.
//   5. Targeting: position=Interstitial plus refresh_count=0 / refresh_bucket=initial so
//      the reporting cuts that filter on those keys keep seeing this slot.
//
// GPT returns null from defineOutOfPageSlot when the format is not allowed in the current
// context (for example inside an iframe); every use of the slot is guarded for that.
// -----------------------------------------------------------------------------

/**
 * Debug mode (opt-in)
 * Enable by adding ?addebug=1 to the page URL.
 *
 * This is intentionally side-effect free when disabled.
 */
var ADS_DEBUG_MODE_ENABLED = false;

try {
    ADS_DEBUG_MODE_ENABLED = /(?:\?|&)addebug=1(?:&|$)/.test(String(window.location && window.location.search));
} catch (error) {
    ADS_DEBUG_MODE_ENABLED = false;
}

/**
 * Internal debug logger. No-ops unless ADS_DEBUG_MODE_ENABLED is true.
 */
function adsDebugLog() {
    if (!ADS_DEBUG_MODE_ENABLED) {
        return;
    }

    // eslint-disable-next-line no-console
    console.log.apply(console, arguments);
}

// Expose a read-only snapshot helper in debug mode.
if (ADS_DEBUG_MODE_ENABLED) {
    window.__adsRefreshDebugStates = window.__adsRefreshDebugStates || {};
    window.__adsDebugState = function () {
        try {
            return JSON.parse(JSON.stringify(window.__adsRefreshDebugStates || {}));
        } catch (error) {
            return window.__adsRefreshDebugStates || {};
        }
    };
}


// ID5 for secure signals sharing in GAM
window.ID5EspConfig = { 
    partnerId: 1355 // ID5 partnerId
};

// Amount of time (ms) that Prebid allows the ATF bidders to respond to ad requests.
var PREBID_TIMEOUT = 1500;

// Amount of time (ms) that Prebid allows the BTF bidders to respond to ad requests.
var BTF_PREBID_TIMEOUT = 1500;

// Number of seconds to wait after the slot becomes viewable.
var SECONDS_TO_WAIT_AFTER_VIEWABILITY = 10;

// Number of seconds to wait between refreshes when the currently rendered advertiser
// is listed in GAM_SLOW_REFRESH_ADVERTISER_IDS below, instead of the default
// SECONDS_TO_WAIT_AFTER_VIEWABILITY. Applies to every ad position on the page.
var SECONDS_TO_WAIT_FOR_SLOW_REFRESH_ADVERTISERS = 30;

// Advertisers for which the refresh interval should be slowed down to
// SECONDS_TO_WAIT_FOR_SLOW_REFRESH_ADVERTISERS seconds instead of the default
// SECONDS_TO_WAIT_AFTER_VIEWABILITY seconds. This does not stop refresh entirely
// (see GAM_NOREFRESH_ADVERTISER_IDS below for that) -- it only slows it down.
// Applies across all ad positions. Just add the company IDs e.g [3219368852, 3130234283].
var GAM_SLOW_REFRESH_ADVERTISER_IDS = [];

// Maximum number of refreshes allowed per non-sticky slot during one page view.
// Sticky / Adhesion is intentionally uncapped.

// Advertisers for which refresh shouldn't apply. Just add the company IDs e.g [3219368852, 3130234283].
var GAM_NOREFRESH_ADVERTISER_IDS = [];

// Campaigns for which refresh shouldn't apply. Just add the order IDs e.g [3219368852, 3130234283].
var GAM_NOREFRESH_ORDER_IDS = [];

// Ad units (div ids) for which refresh shouldn't apply. Just add the div IDs e.g ['Branding_Left', 'Branding_Right'].
var GAM_NOREFRESH_ADUNITS = ['Branding_Left', 'Branding_Right', 'Stories_1', 'Stories_2'];

// How far should the ad be for the lazy loading to activate.
var DISTANCE_IN_PIXELS_FOR_LAZY_LOADING = "700px";

// How much (in percent) should the ad slot be viewable. 
var VISIBILITY_PERCENT_FOR_REFRESH = 0.6;

// Variables for all defined slots. 
var Adhesion;
var Billboard;
var Branding_Left;
var Branding_Right;
var P1;
var P2;
var P3;
var In_Text_1;
var In_Text_2;
var In_Text_3;
var In_Text_4;
var In_Text_5;
var Interstitial; // GPT out-of-page web interstitial (11092026 variant B), no div in the page.

// =============================================================================
// 25092026 -- FIRST-REQUEST TIMING TELEMETRY (passive). See the header.
// =============================================================================

// Bucket edges in milliseconds, shared by both keys. The labels are exactly the
// value list that exists under each key in GAM.
var FIRST_REQUEST_TIMING_BUCKETS = [
    { upTo: 500, label: 'lt_500' },
    { upTo: 1000, label: '500_999' },
    { upTo: 1500, label: '1000_1499' },
    { upTo: 2000, label: '1500_1999' },
    { upTo: 3000, label: '2000_2999' },
    { upTo: 5000, label: '3000_4999' }
];
var FIRST_REQUEST_TIMING_TOP_BUCKET = '5000_plus';
var FIRST_REQUEST_TIMING_UNKNOWN = 'unknown';
var CONSENT_WAIT_PENDING = 'pending';

// GPT key names (network-wide predefined keys, report-on).
var GPT_KEY_FIRST_REQUEST = 'aq_first_request';
var GPT_KEY_CONSENT_WAIT = 'aq_consent_wait';

// How often to re-check whether consent became final AFTER the first ad request
// went out with aq_consent_wait=pending, and for how long.
var CONSENT_WAIT_POLL_MILLISECONDS = 250;
var CONSENT_WAIT_POLL_MAX_MILLISECONDS = 60000;

// How long the consent observer keeps looking for a __tcfapi function that was
// not there when the wrapper started (the CMP loader sits in the page head, so
// normally it is found at once).
var CONSENT_OBSERVER_TCFAPI_POLL_MILLISECONDS = 100;
var CONSENT_OBSERVER_TCFAPI_POLL_MAX_MILLISECONDS = 60000;

// Raw timings for the debug snapshot. Milliseconds since the page's time origin
// (performance.now() clock), null until recorded.
var firstRequestTiming = {
    scriptStartMs: null,
    wrapperStartMs: null,
    wrapperStartReason: null,
    firstRequestBidsMs: null,
    firstRefreshCallMs: null,
    firstRefreshCallSlots: null,
    consentFinalMs: null,
    consentFinalReason: null,
    consentWaitAtFirstRequest: null,
    firstRequestBucket: null,
    consentWaitBucket: null,
    consentWaitBucketUpdatedAtMs: null,
    tcfApiFoundAtMs: null,
    slotRequestedMs: {},
    slotResponseReceivedMs: {},
    slotRenderEndedMs: {}
};

// Milliseconds since the page's time origin; null when the clock is missing.
function timingNowMilliseconds() {
    try {
        if (window.performance && typeof window.performance.now === 'function') {
            return window.performance.now();
        }
    } catch (error) {
        return null;
    }
    return null;
}

// Maps a duration to its GAM bucket label.
function getFirstRequestTimingBucket(milliseconds) {
    if (milliseconds === null || milliseconds === undefined || isNaN(milliseconds)) {
        return FIRST_REQUEST_TIMING_UNKNOWN;
    }
    var bucketIndex;
    for (bucketIndex = 0; bucketIndex < FIRST_REQUEST_TIMING_BUCKETS.length; bucketIndex++) {
        if (milliseconds < FIRST_REQUEST_TIMING_BUCKETS[bucketIndex].upTo) {
            return FIRST_REQUEST_TIMING_BUCKETS[bucketIndex].label;
        }
    }
    return FIRST_REQUEST_TIMING_TOP_BUCKET;
}

// Records the first moment consent stopped being a question for this page.
// Reasons: cmp_tcloaded, cmp_useractioncomplete, gdpr_not_applicable. Set once.
function recordConsentFinal(reason) {
    try {
        if (firstRequestTiming.consentFinalMs !== null) {
            return;
        }
        firstRequestTiming.consentFinalMs = timingNowMilliseconds();
        firstRequestTiming.consentFinalReason = reason;
        adsDebugLog('[first-request] consent final at', Math.round(firstRequestTiming.consentFinalMs || -1), 'ms (' + reason + ')');
    } catch (error) {
        // Telemetry must never throw into the wrapper.
    }
}

// The __tcfapi event callback: notes the consent-final moment, nothing else.
function onTcfEventForTelemetry(tcData, success) {
    try {
        if (!success || !tcData) {
            return;
        }
        if (tcData.gdprApplies === false) {
            recordConsentFinal('gdpr_not_applicable');
            return;
        }
        if (tcData.eventStatus === 'tcloaded') {
            recordConsentFinal('cmp_tcloaded');
            return;
        }
        if (tcData.eventStatus === 'useractioncomplete') {
            recordConsentFinal('cmp_useractioncomplete');
        }
    } catch (error) {
        // Ignore.
    }
}

// Registers the telemetry listener on __tcfapi; keeps looking for the function
// for a while if the CMP loader has not defined it yet. Passive: it only listens.
function installConsentObserver() {
    var waitedMilliseconds = 0;
    function tryRegister() {
        try {
            if (typeof window.__tcfapi === 'function') {
                firstRequestTiming.tcfApiFoundAtMs = timingNowMilliseconds();
                window.__tcfapi('addEventListener', 2, onTcfEventForTelemetry);
                return true;
            }
        } catch (error) {
            return true; // a throwing CMP is not something telemetry should keep poking
        }
        return false;
    }
    try {
        if (tryRegister()) {
            return;
        }
        var pollTimer = setInterval(function () {
            try {
                waitedMilliseconds = waitedMilliseconds + CONSENT_OBSERVER_TCFAPI_POLL_MILLISECONDS;
                if (tryRegister() || waitedMilliseconds >= CONSENT_OBSERVER_TCFAPI_POLL_MAX_MILLISECONDS) {
                    clearInterval(pollTimer);
                }
            } catch (error) {
                clearInterval(pollTimer);
            }
        }, CONSENT_OBSERVER_TCFAPI_POLL_MILLISECONDS);
    } catch (error) {
        // Ignore: the observer is telemetry only.
    }
}

// Returns the consent-final moment, or null while consent is still pending.
function readConsentFinal() {
    return typeof firstRequestTiming.consentFinalMs === 'number' ? firstRequestTiming.consentFinalMs : null;
}

// Records a named milestone once (first call wins).
function recordFirstRequestMilestone(fieldName, extraFieldName, extraValue) {
    try {
        if (firstRequestTiming[fieldName] !== null) {
            return;
        }
        firstRequestTiming[fieldName] = timingNowMilliseconds();
        if (extraFieldName) {
            firstRequestTiming[extraFieldName] = extraValue;
        }
        adsDebugLog('[first-request] ' + fieldName, Math.round(firstRequestTiming[fieldName] || -1), extraValue || '');
    } catch (error) {
        // Telemetry must never throw into the wrapper.
    }
}

// Writes the consent-wait key-value at page level. Called at the first ad
// request and again if consent becomes final later (pending -> bucket).
function applyConsentWaitTargeting(bucketLabel) {
    try {
        googletag.pubads().setTargeting(GPT_KEY_CONSENT_WAIT, bucketLabel);
        firstRequestTiming.consentWaitBucket = bucketLabel;
        firstRequestTiming.consentWaitBucketUpdatedAtMs = timingNowMilliseconds();
        adsDebugLog('[first-request] ' + GPT_KEY_CONSENT_WAIT + '=' + bucketLabel);
    } catch (error) {
        // Ignore: page-level targeting is optional telemetry.
    }
}

// Polls the consent notes after a 'pending' first request so the page's later
// ad requests carry the real consent-wait bucket.
function startConsentWaitResolutionPoll() {
    var waitedMilliseconds = 0;
    var pollTimer = setInterval(function () {
        try {
            waitedMilliseconds = waitedMilliseconds + CONSENT_WAIT_POLL_MILLISECONDS;
            var consentFinalMs = readConsentFinal();
            if (consentFinalMs !== null) {
                clearInterval(pollTimer);
                applyConsentWaitTargeting(getFirstRequestTimingBucket(consentFinalMs));
                return;
            }
            if (waitedMilliseconds >= CONSENT_WAIT_POLL_MAX_MILLISECONDS) {
                clearInterval(pollTimer);
            }
        } catch (error) {
            clearInterval(pollTimer);
        }
    }, CONSENT_WAIT_POLL_MILLISECONDS);
}

// Called immediately before EVERY googletag.pubads().refresh() call (the initial
// single-request call, lazy loads, refreshes). Only the first call sets
// aq_first_request; later calls only upgrade aq_consent_wait from 'pending'.
function recordFirstRequestTelemetryBeforeRefresh(slotDescription) {
    try {
        var consentFinalMs = readConsentFinal();

        if (firstRequestTiming.firstRefreshCallMs === null) {
            firstRequestTiming.firstRefreshCallMs = timingNowMilliseconds();
            firstRequestTiming.firstRefreshCallSlots = slotDescription;
            firstRequestTiming.firstRequestBucket = getFirstRequestTimingBucket(firstRequestTiming.firstRefreshCallMs);
            googletag.pubads().setTargeting(GPT_KEY_FIRST_REQUEST, firstRequestTiming.firstRequestBucket);
            adsDebugLog('[first-request] ' + GPT_KEY_FIRST_REQUEST + '=' + firstRequestTiming.firstRequestBucket,
                'at', Math.round(firstRequestTiming.firstRefreshCallMs || -1), 'ms, slots', slotDescription);

            if (consentFinalMs !== null) {
                firstRequestTiming.consentWaitAtFirstRequest = getFirstRequestTimingBucket(consentFinalMs);
                applyConsentWaitTargeting(firstRequestTiming.consentWaitAtFirstRequest);
            } else {
                firstRequestTiming.consentWaitAtFirstRequest = CONSENT_WAIT_PENDING;
                applyConsentWaitTargeting(CONSENT_WAIT_PENDING);
                startConsentWaitResolutionPoll();
            }
            return;
        }

        // Not the first request: only upgrade a still-pending consent value.
        if (firstRequestTiming.consentWaitBucket === CONSENT_WAIT_PENDING && consentFinalMs !== null) {
            applyConsentWaitTargeting(getFirstRequestTimingBucket(consentFinalMs));
        }
    } catch (error) {
        // Telemetry must never throw into the wrapper.
    }
}

// GPT lifecycle listeners for the debug snapshot: first request / response /
// render time per slot. Registered through the GPT queue so pubads() exists.
function installFirstRequestGptListeners() {
    try {
        googletag.cmd.push(function () {
            function slotIdOf(event) {
                try {
                    return event.slot.getSlotElementId();
                } catch (error) {
                    return 'unknown';
                }
            }
            function recordSlotEvent(store, event) {
                try {
                    var slotId = slotIdOf(event);
                    if (store[slotId] === undefined) {
                        store[slotId] = timingNowMilliseconds();
                    }
                } catch (error) {
                    // Ignore.
                }
            }
            googletag.pubads().addEventListener('slotRequested', function (event) {
                recordSlotEvent(firstRequestTiming.slotRequestedMs, event);
            });
            googletag.pubads().addEventListener('slotResponseReceived', function (event) {
                recordSlotEvent(firstRequestTiming.slotResponseReceivedMs, event);
            });
            googletag.pubads().addEventListener('slotRenderEnded', function (event) {
                recordSlotEvent(firstRequestTiming.slotRenderEndedMs, event);
            });
        });
    } catch (error) {
        // Ignore: listeners are debug-only.
    }
}

// Debug snapshot for live verification: window.__pulsTimingSnapshot().
window.__pulsTimingSnapshot = function () {
    try {
        var navigationTiming = null;
        try {
            var navigationEntries = window.performance.getEntriesByType('navigation');
            if (navigationEntries && navigationEntries.length > 0) {
                navigationTiming = {
                    domContentLoadedEventEndMs: navigationEntries[0].domContentLoadedEventEnd,
                    domInteractiveMs: navigationEntries[0].domInteractive,
                    responseEndMs: navigationEntries[0].responseEnd
                };
            }
        } catch (error) {
            navigationTiming = null;
        }
        var snapshot = JSON.parse(JSON.stringify(firstRequestTiming));
        snapshot.navigation = navigationTiming;
        // 01102026-2: the consent fast path state, copied so the snapshot is self-contained.
        try {
            snapshot.consentFastPath = JSON.parse(JSON.stringify(window.__pulsConsentFastPath || null));
        } catch (fastPathCopyError) {
            snapshot.consentFastPath = null;
        }
        snapshot.nowMs = timingNowMilliseconds();
        return snapshot;
    } catch (error) {
        return { error: String(error) };
    }
};

// =============================================================================
// 01102026-2 -- NON-EEA CONSENT FAST PATH (arm A: the fast path ALONE, no early
//               auction). Ported unchanged from the 30092026 / 01102026 arm B.
//               See the file header.
// =============================================================================
//
// Both consent-aware ad systems on this page hold their work until the TCF API
// (window.__tcfapi) gives a final answer: Prebid's consent module does not start
// an auction before it, and GPT does not send an ad request before it. The CMP
// answers "GDPR does not apply" for this site's non-EEA visitors, but only once
// its own scripts and configuration have loaded (measured live: consent final at
// a median of ~1.5 s after navigation, and the auction always started 70-90 ms
// after it). This section answers that question at once for a visitor the page
// template has stamped as non-EEA, so the first auction and the first ad request
// start at script execution instead of at the CMP's answer.
//
// The CMP itself is untouched: it still loads, still decides on its own, its
// real answer still reaches every consumer afterwards, and that real answer
// takes precedence the moment it arrives.
//
// When it applies -- ALL of these must hold, otherwise nothing is installed and
// the page behaves exactly as without this section:
//
//   1. CONSENT_FAST_PATH_ENABLED is true.
//   2. The template's country stamp (window.adsVisitorCountry, decided
//      server-side, ISO 3166-1 alpha-2) is one of CONSENT_FAST_PATH_COUNTRIES.
//      A missing, empty or any other value means no fast path.
//   3. The device timezone is not an unambiguous EEA/CH/UK zone. This is a
//      fail-safe against a wrong stamp (a cached page, a proxy, a template
//      mistake): a device set to Berlin, Vienna, Zurich, Paris ... is never
//      answered synthetically, whatever the stamp says.
//   4. window.__tcfapi exists and its synchronous "ping" reports a CMP that has
//      not finished loading (cmpStatus "stub" or "loading"). A CMP that already
//      reports "loaded" is about to answer on its own.
//
// How it works once installed:
//
//   - window.__tcfapi becomes an accessor property: its getter returns the proxy
//     below, its setter captures the CMP's own function when the CMP core
//     installs it later (the core overwrites window.__tcfapi when it loads). The
//     proxy therefore stays in front of the CMP for the whole page life, and
//     every call still reaches the real CMP. A poll re-installs the accessor if
//     the property is ever redefined outright.
//
//   - "addEventListener" is forwarded to the CMP (so the consumer receives the
//     CMP's real events later) AND answered immediately with a synthetic final
//     TCData (gdprApplies = false, eventStatus "tcloaded"). Consumers that were
//     already waiting when this file ran -- in the CMP stub's command queue or in
//     the loaded CMP's listener registry (GPT registers on load, before this
//     file) -- are answered the same way and re-wrapped in place so the CMP's
//     later events reach them through the same filter.
//
//   - "getTCData" is answered synthetically while the fast path is in force.
//
//   - While in force, the CMP's non-final events (cmpStatus "loading") are not
//     forwarded to consumers that already hold the synthetic final answer; the
//     CMP's final event ("tcloaded" / "useractioncomplete") and a banner-shown
//     event ("cmpuishown") always are. A real final event ENDS the fast path:
//     from then on the proxy is a pure pass-through and the CMP's answer is the
//     only one in effect. If the CMP disagrees with the stamp (an EEA visitor
//     after all), its banner and its final answer govern every later request.
//
//   - The telemetry's consent-final moment is recorded at install with the
//     reason "fast_path_<country>", so aq_consent_wait on this arm measures the
//     fast path directly.
//
// Every step is wrapped in try/catch: a surprise in the CMP's shape can only
// switch the fast path off, never break consent handling or ad serving.
//
// Debugging: window.__pulsConsentFastPath (live state) and
// window.__pulsTimingSnapshot().consentFastPath (copy in the snapshot).
// =============================================================================

// Kill switch for the fast path (this arm has no early auction).
var CONSENT_FAST_PATH_ENABLED = true;

// The globals the page template may stamp with the visitor's country before
// the ads tag (server-side geolocation, ISO 3166-1 alpha-2, e.g. "RS"), in the
// order they are tried. The head template writes window.adsVisitorCountry on
// both CMS variants (since 01.10.2026). Only that name is read since the
// 05102026 build; a page without it is simply never fast-pathed (no_stamp).
var CONSENT_FAST_PATH_COUNTRY_GLOBALS = ['adsVisitorCountry'];

// Strict allow-list: a visitor is fast-pathed only when the stamp is one of
// these. Everything else -- EEA states, UK, CH, unknown, empty, missing -- is
// left entirely to the CMP, exactly as on arm A.
var CONSENT_FAST_PATH_COUNTRIES = ['RS', 'BA', 'ME', 'MK', 'XK', 'AL'];

// Device timezones that identify an EEA/CH/UK device beyond reasonable doubt.
// A device on one of these is never fast-pathed, whatever the stamp says. The
// list deliberately omits every zone a Serbian device commonly reports:
// Europe/Belgrade itself, and the Windows group-default zones Europe/Budapest
// (Belgrade/Bratislava/Budapest/Ljubljana/Prague group) and Europe/Warsaw
// (Sarajevo/Skopje/Warsaw/Zagreb group) plus the members of those groups.
var CONSENT_FAST_PATH_EEA_DEVICE_TIMEZONES = [
    'Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'Europe/Amsterdam',
    'Europe/Brussels', 'Europe/Luxembourg', 'Europe/Paris', 'Europe/Madrid',
    'Europe/Rome', 'Europe/Malta', 'Europe/Copenhagen', 'Europe/Stockholm',
    'Europe/Oslo', 'Europe/Helsinki', 'Europe/Athens', 'Europe/Bucharest',
    'Europe/Sofia', 'Europe/Vilnius', 'Europe/Riga', 'Europe/Tallinn',
    'Europe/Dublin', 'Europe/London', 'Europe/Lisbon', 'Atlantic/Reykjavik',
    'Atlantic/Madeira', 'Atlantic/Azores', 'Atlantic/Canary', 'Europe/Nicosia',
    'Asia/Nicosia', 'Europe/Vaduz'
];

// After the CMP core replaces window.__tcfapi the proxy must stay in front of
// it. The setter of the accessor handles a plain assignment; this poll handles
// an outright property redefinition, and also re-checks the CMP's queue and
// registry for consumers that registered past the proxy.
var CONSENT_FAST_PATH_POLL_MILLISECONDS = 100;
var CONSENT_FAST_PATH_POLL_MAX_MILLISECONDS = 8000;

// Synthetic listener ids start high so they can never collide with the ids the
// real CMP hands out.
var CONSENT_FAST_PATH_SYNTHETIC_LISTENER_ID_START = 100000;

// Live state, read-only for debugging (window.__pulsConsentFastPath).
var consentFastPathState = {
    enabled: CONSENT_FAST_PATH_ENABLED,
    stamp: null,                    // the raw stamp value as found on the page
    country: null,                  // the normalised country, or null
    timezone: null,                 // the device timezone as read
    cmpStatusAtInstall: null,       // the CMP's ping cmpStatus when this file ran
    applied: false,                 // true once the proxy is installed
    skippedReason: null,            // why the fast path was not installed
    appliedAtMs: null,              // install moment (page time origin clock)
    accessorInstalled: null,        // true = accessor property, false = plain assignment fallback
    queuedListenersAnswered: 0,     // consumers found waiting in the CMP queue / registry
    proxiedListenersAnswered: 0,    // consumers that registered through the proxy
    cmpReplacements: 0,             // how often the CMP assigned a new __tcfapi function
    rewraps: 0,                     // how often the poll had to re-install the accessor
    cmpFinal: false,                // true once the real CMP gave a final answer
    cmpFinalAtMs: null,
    cmpFinalGdprApplies: null,      // the real CMP's gdprApplies at its final answer
    cmpFinalEventStatus: null
};
window.__pulsConsentFastPath = consentFastPathState;

// The CMP's own __tcfapi function behind the proxy: the stub at install, the
// CMP core once it loads (captured by the accessor's setter).
var consentFastPathRealTcfApi = null;

// Every consumer the fast path knows about, keyed by the consumer's own callback
// function: { callback, syntheticListenerId, realListenerId }.
var consentFastPathConsumers = [];

var consentFastPathNextListenerId = CONSENT_FAST_PATH_SYNTHETIC_LISTENER_ID_START;
var consentFastPathPollTimer = null;

function consentFastPathLog(message) {
    adsDebugLog('[consent-fast-path] ' + message);
}

// True while the synthetic answers are in force: installed, and the real CMP
// has not given its final answer yet.
function consentFastPathInForce() {
    return consentFastPathState.applied === true && consentFastPathState.cmpFinal === false;
}

// Reads and normalises the template's country stamp. Returns the two-letter
// upper-case code, or null when the stamp is missing or unusable.
function readConsentFastPathCountry() {
    try {
        // First global in the list that carries a value wins; the name it was
        // found under is kept for debugging (stampGlobal).
        var rawValue;
        var globalIndex;
        for (globalIndex = 0; globalIndex < CONSENT_FAST_PATH_COUNTRY_GLOBALS.length; globalIndex++) {
            rawValue = window[CONSENT_FAST_PATH_COUNTRY_GLOBALS[globalIndex]];
            if (rawValue !== undefined && rawValue !== null) {
                break;
            }
        }
        if (rawValue === undefined || rawValue === null) {
            consentFastPathState.stamp = null;
            return null;
        }
        consentFastPathState.stampGlobal = CONSENT_FAST_PATH_COUNTRY_GLOBALS[globalIndex];
        consentFastPathState.stamp = String(rawValue);
        if (typeof rawValue !== 'string') {
            return null;
        }
        var normalised = rawValue.replace(/^\s+|\s+$/g, '').toUpperCase();
        if (!/^[A-Z]{2}$/.test(normalised)) {
            return null;
        }
        return normalised;
    } catch (error) {
        return null;
    }
}

function isConsentFastPathCountry(country) {
    var index;
    if (country === null) {
        return false;
    }
    for (index = 0; index < CONSENT_FAST_PATH_COUNTRIES.length; index++) {
        if (CONSENT_FAST_PATH_COUNTRIES[index] === country) {
            return true;
        }
    }
    return false;
}

// The device timezone, or null when the browser cannot tell.
function readConsentFastPathTimezone() {
    try {
        var intlObject = window.Intl || (typeof Intl !== 'undefined' ? Intl : null);
        if (!intlObject || typeof intlObject.DateTimeFormat !== 'function') {
            return null;
        }
        var resolved = intlObject.DateTimeFormat().resolvedOptions();
        if (resolved && typeof resolved.timeZone === 'string' && resolved.timeZone.length > 0) {
            return resolved.timeZone;
        }
    } catch (timezoneError) {
        return null;
    }
    return null;
}

function consentFastPathDeviceLooksEea(timezone) {
    var index;
    if (timezone === null) {
        // Unknown timezone (very old browser): cannot tell, the stamp decides.
        return false;
    }
    for (index = 0; index < CONSENT_FAST_PATH_EEA_DEVICE_TIMEZONES.length; index++) {
        if (CONSENT_FAST_PATH_EEA_DEVICE_TIMEZONES[index] === timezone) {
            return true;
        }
    }
    return false;
}

// Asks the CMP for its status. The CMP stub and the CMP core both answer "ping"
// synchronously; anything else returns null (treated as unknown = no fast path).
function pingTcfApiSynchronously(tcfApiFunction) {
    var pingData = null;
    try {
        tcfApiFunction('ping', 2, function (data, success) {
            if (success !== false && data && typeof data === 'object') {
                pingData = data;
            }
        });
    } catch (pingError) {
        return null;
    }
    return pingData;
}

// A minimal but complete TCData object representing "GDPR does not apply" --
// the same resolution the CMP gives this site's non-EEA visitors. Consumers only
// read gdprApplies / eventStatus / cmpStatus in this case, but the usual
// containers are present so nothing trips over a missing field.
function buildConsentFastPathTcData(listenerId) {
    return {
        // Marker so this file's own observers can tell a synthetic answer from a
        // genuine CMP event; a harmless extra field for every other consumer.
        syntheticFastPathAnswer: true,
        tcString: '',
        tcfPolicyVersion: 4,
        cmpId: 31,
        cmpVersion: 1,
        gdprApplies: false,
        eventStatus: 'tcloaded',
        cmpStatus: 'loaded',
        isServiceSpecific: true,
        useNonStandardTexts: false,
        publisherCC: consentFastPathState.country || 'RS',
        purposeOneTreatment: false,
        listenerId: listenerId,
        purpose: { consents: {}, legitimateInterests: {} },
        vendor: { consents: {}, legitimateInterests: {} },
        specialFeatureOptins: {},
        publisher: {
            consents: {},
            legitimateInterests: {},
            customPurpose: { consents: {}, legitimateInterests: {} },
            restrictions: {}
        }
    };
}

// A real CMP event that must reach a consumer even while the synthetic answer
// is in force: the CMP's own final answer, a non-GDPR answer, or the banner.
function isConsentFastPathForwardableEvent(tcData) {
    if (!tcData || typeof tcData !== 'object') {
        return false;
    }
    if (tcData.gdprApplies === false) {
        return true;
    }
    return tcData.eventStatus === 'tcloaded' ||
        tcData.eventStatus === 'useractioncomplete' ||
        tcData.eventStatus === 'cmpuishown';
}

// Watches genuine CMP events. A final one ends the fast path for good.
function observeConsentFastPathRealEvent(tcData, success) {
    try {
        if (!tcData || typeof tcData !== 'object' || tcData.syntheticFastPathAnswer === true) {
            return;
        }
        if (success === false) {
            return;
        }
        var isFinal = tcData.eventStatus === 'tcloaded' ||
            tcData.eventStatus === 'useractioncomplete' ||
            (tcData.gdprApplies === false && tcData.cmpStatus === 'loaded');
        if (!isFinal || consentFastPathState.cmpFinal === true) {
            return;
        }
        consentFastPathState.cmpFinal = true;
        consentFastPathState.cmpFinalAtMs = timingNowMilliseconds();
        consentFastPathState.cmpFinalGdprApplies = tcData.gdprApplies;
        consentFastPathState.cmpFinalEventStatus = tcData.eventStatus || null;
        stopConsentFastPathPoll();
        consentFastPathLog('real CMP answer is final (gdprApplies=' + tcData.gdprApplies + ', ' + tcData.eventStatus +
            ') at ' + Math.round(consentFastPathState.cmpFinalAtMs || -1) + ' ms; proxy is pass-through from now on');
    } catch (observeError) {
        // Never let an observer break a consumer's callback chain.
    }
}

function findConsentFastPathConsumer(callback) {
    var index;
    for (index = 0; index < consentFastPathConsumers.length; index++) {
        if (consentFastPathConsumers[index].callback === callback) {
            return consentFastPathConsumers[index];
        }
    }
    return null;
}

// Wraps a consumer callback so the CMP's later events reach it through the
// fast-path filter. The wrapper is what the real CMP holds from then on.
function buildConsentFastPathForwarder(consumerEntry) {
    var forwarder = function consentFastPathForwarder(tcData, success) {
        observeConsentFastPathRealEvent(tcData, success);
        try {
            if (tcData && typeof tcData === 'object' && typeof tcData.listenerId === 'number' && tcData.syntheticFastPathAnswer !== true) {
                consumerEntry.realListenerId = tcData.listenerId;
            }
        } catch (idError) {
            // ignore
        }
        if (consentFastPathInForce() && !isConsentFastPathForwardableEvent(tcData)) {
            // The consumer already holds the synthetic final answer; a "loading"
            // ping from the CMP would only make it wait again.
            return;
        }
        consumerEntry.callback(tcData, success);
    };
    // Marks the wrapper so the queue/registry passes never treat it as a new consumer.
    forwarder.pulsConsentFastPathForwarder = true;
    return forwarder;
}

// Hands one consumer the synthetic final answer. Returns the consumer entry.
function answerConsentFastPathConsumer(callback, source) {
    var consumerEntry = findConsentFastPathConsumer(callback);
    if (consumerEntry === null) {
        consumerEntry = { callback: callback, syntheticListenerId: consentFastPathNextListenerId, realListenerId: null, source: source };
        consentFastPathNextListenerId = consentFastPathNextListenerId + 1;
        consentFastPathConsumers.push(consumerEntry);
    }
    try {
        callback(buildConsentFastPathTcData(consumerEntry.syntheticListenerId), true);
    } catch (consumerError) {
        consentFastPathLog('a consumer threw while receiving the synthetic answer (' + source + '): ' + consumerError);
    }
    if (source === 'proxy') {
        consentFastPathState.proxiedListenersAnswered = consentFastPathState.proxiedListenersAnswered + 1;
    } else {
        consentFastPathState.queuedListenersAnswered = consentFastPathState.queuedListenersAnswered + 1;
    }
    return consumerEntry;
}

// Consumers that registered BEFORE this file ran are waiting in one of two
// places: the CMP stub's command queue (window.__cmp.a, entries shaped
// ['addEventListener', 2, callback]) while the CMP core has not loaded, or the
// loaded core's listener registry (window.cmpmngr.api.events, entries shaped
// { type: 'tcfv2', listener, id }). Each is answered synthetically once and
// re-wrapped in place, so the CMP's later events reach it through the filter.
// Any unexpected shape simply skips that source; the proxy still covers every
// consumer that registers from now on.
function answerWaitingConsentConsumers() {
    var answeredCount = 0;
    if (!consentFastPathInForce()) {
        return 0;
    }
    // (a) the stub's command queue
    try {
        var commandQueue = null;
        if (window.__cmp && Object.prototype.toString.call(window.__cmp.a) === '[object Array]') {
            commandQueue = window.__cmp.a;
        } else if (typeof consentFastPathRealTcfApi === 'function') {
            var returnedQueue = consentFastPathRealTcfApi();
            if (Object.prototype.toString.call(returnedQueue) === '[object Array]') {
                commandQueue = returnedQueue;
            }
        }
        if (commandQueue !== null) {
            var queueIndex;
            for (queueIndex = 0; queueIndex < commandQueue.length; queueIndex++) {
                var queuedCall = commandQueue[queueIndex];
                if (!queuedCall || typeof queuedCall.length !== 'number' || queuedCall.length < 3) {
                    continue;
                }
                var queuedCommand = queuedCall[0];
                var queuedVersion = queuedCall[1];
                var queuedCallback = queuedCall[2];
                if (typeof queuedCallback !== 'function' || queuedCallback.pulsConsentFastPathForwarder === true) {
                    continue;
                }
                if (!(queuedVersion === 2 || queuedVersion === '2')) {
                    continue;
                }
                if (queuedCommand === 'addEventListener') {
                    if (findConsentFastPathConsumer(queuedCallback) !== null) {
                        continue;
                    }
                    var queuedEntry = answerConsentFastPathConsumer(queuedCallback, 'stub_queue');
                    queuedCall[2] = buildConsentFastPathForwarder(queuedEntry);
                    answeredCount = answeredCount + 1;
                } else if (queuedCommand === 'getTCData') {
                    if (findConsentFastPathConsumer(queuedCallback) !== null) {
                        continue;
                    }
                    // One-shot request: answered now; the CMP answers it again for
                    // real later, which is the answer that should win anyway.
                    answerConsentFastPathConsumer(queuedCallback, 'stub_queue');
                    answeredCount = answeredCount + 1;
                }
            }
        }
    } catch (queueError) {
        consentFastPathLog('stub queue pass skipped: ' + queueError);
    }
    // (b) the loaded core's listener registry
    try {
        var cmpVendorObject = window.cmpmngr;
        if (cmpVendorObject && cmpVendorObject.api && cmpVendorObject.api.events && typeof cmpVendorObject.api.events.forEach === 'function') {
            cmpVendorObject.api.events.forEach(function (registryEntry) {
                if (!registryEntry || registryEntry.type !== 'tcfv2' || typeof registryEntry.listener !== 'function') {
                    return;
                }
                if (registryEntry.listener.pulsConsentFastPathForwarder === true) {
                    return;
                }
                if (findConsentFastPathConsumer(registryEntry.listener) !== null) {
                    return;
                }
                var registryConsumer = answerConsentFastPathConsumer(registryEntry.listener, 'cmp_registry');
                if (typeof registryEntry.id === 'number') {
                    registryConsumer.realListenerId = registryEntry.id;
                }
                registryEntry.listener = buildConsentFastPathForwarder(registryConsumer);
                answeredCount = answeredCount + 1;
            });
        }
    } catch (registryError) {
        consentFastPathLog('CMP registry pass skipped: ' + registryError);
    }
    return answeredCount;
}

// The proxy that fronts window.__tcfapi. Signature per the TCF v2 API:
// (command, version, callback, parameter). Called with no arguments it hands
// back whatever the real function returns (the stub returns its queue that way,
// and the CMP core collects it when it loads).
function consentFastPathProxy(command, version, callback, parameter) {
    var realTcfApi = consentFastPathRealTcfApi;
    if (arguments.length === 0) {
        return typeof realTcfApi === 'function' ? realTcfApi() : undefined;
    }

    // --- addEventListener --------------------------------------------------
    if (command === 'addEventListener' && typeof callback === 'function') {
        var consumerEntry = findConsentFastPathConsumer(callback);
        if (consumerEntry === null) {
            consumerEntry = { callback: callback, syntheticListenerId: consentFastPathNextListenerId, realListenerId: null, source: 'proxy' };
            consentFastPathNextListenerId = consentFastPathNextListenerId + 1;
            consentFastPathConsumers.push(consumerEntry);
        }
        // Always register with the real CMP first, so the consumer is driven by
        // the CMP's real events later (and immediately, once the CMP is final).
        if (typeof realTcfApi === 'function') {
            try {
                realTcfApi(command, version, buildConsentFastPathForwarder(consumerEntry), parameter);
            } catch (forwardError) {
                consentFastPathLog('forwarding addEventListener to the CMP failed: ' + forwardError);
            }
        }
        if (consentFastPathInForce()) {
            try {
                callback(buildConsentFastPathTcData(consumerEntry.syntheticListenerId), true);
            } catch (consumerError) {
                consentFastPathLog('a consumer threw while receiving the synthetic answer (proxy): ' + consumerError);
            }
            consentFastPathState.proxiedListenersAnswered = consentFastPathState.proxiedListenersAnswered + 1;
        }
        return undefined;
    }

    // --- getTCData -----------------------------------------------------------
    if (command === 'getTCData' && typeof callback === 'function') {
        if (consentFastPathInForce()) {
            try {
                callback(buildConsentFastPathTcData(null), true);
            } catch (consumerError) {
                consentFastPathLog('a consumer threw while receiving the synthetic getTCData answer: ' + consumerError);
            }
            return undefined;
        }
        if (typeof realTcfApi === 'function') {
            return realTcfApi.apply(null, arguments);
        }
        return undefined;
    }

    // --- removeEventListener -------------------------------------------------
    // A synthetic id is confirmed locally, and the matching real registration
    // is removed from the CMP too once its real id is known.
    if (command === 'removeEventListener') {
        var removeIndex;
        for (removeIndex = 0; removeIndex < consentFastPathConsumers.length; removeIndex++) {
            if (consentFastPathConsumers[removeIndex].syntheticListenerId === parameter) {
                var removedEntry = consentFastPathConsumers.splice(removeIndex, 1)[0];
                if (removedEntry.realListenerId !== null && typeof realTcfApi === 'function') {
                    try {
                        realTcfApi('removeEventListener', version, function () {}, removedEntry.realListenerId);
                    } catch (removeError) {
                        // ignore
                    }
                }
                if (typeof callback === 'function') {
                    try {
                        callback(true);
                    } catch (callbackError) {
                        // ignore
                    }
                }
                return undefined;
            }
        }
        if (typeof realTcfApi === 'function') {
            return realTcfApi.apply(null, arguments);
        }
        return undefined;
    }

    // --- everything else (ping, getInAppTCData, vendor commands ...) ---------
    if (typeof realTcfApi === 'function') {
        return realTcfApi.apply(null, arguments);
    }
    return undefined;
}

// Puts the proxy in front of the given real function. The accessor keeps the
// proxy in place across the CMP core's own `window.__tcfapi = ...` assignment;
// the setter just captures the new real function.
function installConsentFastPathAccessor(currentRealTcfApi) {
    consentFastPathRealTcfApi = currentRealTcfApi;
    try {
        // The stub keeps its postMessage handler on the function itself and the
        // CMP core looks it up there when it takes over; keep it reachable.
        if (currentRealTcfApi && typeof currentRealTcfApi.msgHandler === 'function' && typeof consentFastPathProxy.msgHandler !== 'function') {
            consentFastPathProxy.msgHandler = currentRealTcfApi.msgHandler;
        }
    } catch (propertyError) {
        // ignore
    }
    try {
        Object.defineProperty(window, '__tcfapi', {
            configurable: true,
            enumerable: true,
            get: function () {
                return consentFastPathProxy;
            },
            set: function (newValue) {
                if (typeof newValue === 'function' && newValue !== consentFastPathProxy) {
                    consentFastPathRealTcfApi = newValue;
                    consentFastPathState.cmpReplacements = consentFastPathState.cmpReplacements + 1;
                }
            }
        });
        consentFastPathState.accessorInstalled = true;
    } catch (defineError) {
        // Engine without accessor support on window: plain assignment, and the
        // poll below re-wraps if the CMP replaces it.
        window.__tcfapi = consentFastPathProxy;
        consentFastPathState.accessorInstalled = false;
    }
}

function stopConsentFastPathPoll() {
    if (consentFastPathPollTimer !== null) {
        clearInterval(consentFastPathPollTimer);
        consentFastPathPollTimer = null;
    }
}

// Keeps the proxy in front of the CMP and keeps answering late-found waiting
// consumers, until the CMP's real answer is final or the window closes.
function startConsentFastPathPoll() {
    var waitedMilliseconds = 0;
    consentFastPathPollTimer = setInterval(function () {
        try {
            waitedMilliseconds = waitedMilliseconds + CONSENT_FAST_PATH_POLL_MILLISECONDS;
            if (!consentFastPathInForce() || waitedMilliseconds >= CONSENT_FAST_PATH_POLL_MAX_MILLISECONDS) {
                stopConsentFastPathPoll();
                return;
            }
            var descriptor = Object.getOwnPropertyDescriptor(window, '__tcfapi');
            if (descriptor && typeof descriptor.get !== 'function') {
                // The property was redefined as a plain value (not assigned): put
                // the proxy back in front of whatever function is there now.
                if (typeof descriptor.value === 'function' && descriptor.value !== consentFastPathProxy) {
                    installConsentFastPathAccessor(descriptor.value);
                    consentFastPathState.rewraps = consentFastPathState.rewraps + 1;
                    consentFastPathLog('window.__tcfapi was redefined; proxy re-installed in front of the new function');
                }
            }
            answerWaitingConsentConsumers();
        } catch (pollError) {
            stopConsentFastPathPoll();
        }
    }, CONSENT_FAST_PATH_POLL_MILLISECONDS);
}

function skipConsentFastPath(reason) {
    consentFastPathState.skippedReason = reason;
    consentFastPathLog('not applied: ' + reason);
}

// Decides and, when every condition holds, installs the fast path. Runs once,
// as early as this file executes, before the consent observer and before any
// requestBids call.
function installConsentFastPath() {
    try {
        if (!CONSENT_FAST_PATH_ENABLED) {
            skipConsentFastPath('disabled');
            return;
        }
        var country = readConsentFastPathCountry();
        consentFastPathState.country = country;
        if (country === null) {
            skipConsentFastPath(consentFastPathState.stamp === null ? 'no_stamp' : 'unusable_stamp');
            return;
        }
        if (!isConsentFastPathCountry(country)) {
            skipConsentFastPath('country_not_listed:' + country);
            return;
        }
        var timezone = readConsentFastPathTimezone();
        consentFastPathState.timezone = timezone;
        if (consentFastPathDeviceLooksEea(timezone)) {
            skipConsentFastPath('eea_device_timezone:' + timezone);
            return;
        }
        var currentTcfApi = window.__tcfapi;
        if (typeof currentTcfApi !== 'function') {
            skipConsentFastPath('no_tcfapi');
            return;
        }
        var pingData = pingTcfApiSynchronously(currentTcfApi);
        if (pingData === null) {
            skipConsentFastPath('ping_unanswered');
            return;
        }
        consentFastPathState.cmpStatusAtInstall = typeof pingData.cmpStatus === 'string' ? pingData.cmpStatus : null;
        if (pingData.cmpStatus === 'loaded') {
            skipConsentFastPath('cmp_already_loaded');
            return;
        }

        installConsentFastPathAccessor(currentTcfApi);
        consentFastPathState.applied = true;
        consentFastPathState.appliedAtMs = timingNowMilliseconds();
        // For this page, consent stopped being a question right now.
        recordConsentFinal('fast_path_' + country);

        // This file's own observer, registered straight with the real CMP: it
        // learns when the CMP reaches its own final answer. It is known to the
        // consumer list up front so the queue pass never answers it.
        try {
            var realAnswerObserver = function (tcData, success) {
                observeConsentFastPathRealEvent(tcData, success);
            };
            realAnswerObserver.pulsConsentFastPathForwarder = true;
            consentFastPathConsumers.push({ callback: realAnswerObserver, syntheticListenerId: -1, realListenerId: null, source: 'internal' });
            currentTcfApi('addEventListener', 2, realAnswerObserver);
        } catch (observerError) {
            consentFastPathLog('could not register the real-answer observer: ' + observerError);
        }

        var waitingAnswered = answerWaitingConsentConsumers();
        startConsentFastPathPoll();
        consentFastPathLog('applied for ' + country + ' (CMP ' + consentFastPathState.cmpStatusAtInstall + ', timezone ' + timezone +
            ', ' + waitingAnswered + ' waiting consumer(s) answered) at ' + Math.round(consentFastPathState.appliedAtMs || -1) + ' ms');
    } catch (installError) {
        skipConsentFastPath('error:' + installError);
    }
}

// Script-start milestone, consent fast path, consent observer and GPT listeners:
// installed as soon as this file runs (before the wrapper boots).
recordFirstRequestMilestone('scriptStartMs');
installConsentFastPath(); // 01102026-2: must precede the consent observer and any requestBids
installConsentObserver();
installFirstRequestGptListeners();

// Top level ad unit path. 
var adUnitPath = "/161634325/Pulsonline.rs/";

var adWindowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth; //check user's screen width

// ATF and BTF array variables. 
var adUnitsATF = [];
var adUnitsBTF = [];


function getPrebidTimeoutForSlot(divId) {
    var btfSlots = ['P2', 'P3', 'In_Text_2', 'In_Text_3', 'In_Text_4', 'In_Text_5'];
    return btfSlots.indexOf(divId) !== -1 ? BTF_PREBID_TIMEOUT : PREBID_TIMEOUT;
}


// Reads the advertiser ID of the currently rendered creative for a slot, if any.
// Shared by the refresh-interval selection below and by the no-refresh advertiser
// check in attemptSlotRefresh, so the same lookup logic lives in exactly one place.
function getCurrentAdvertiserIdForSlot(slotAd) {
    var advertiserID;

    try {
        advertiserID = slotAd.getResponseInformation().advertiserId;
    } catch (error) {
        // Response information can be missing for empty slots or before first render.
    }

    return advertiserID;
}


// Decides how many seconds to wait before the next refresh for this slot.
// Most advertisers use the default SECONDS_TO_WAIT_AFTER_VIEWABILITY cadence.
// Advertisers listed in GAM_SLOW_REFRESH_ADVERTISER_IDS get the slower
// SECONDS_TO_WAIT_FOR_SLOW_REFRESH_ADVERTISERS cadence instead, on every position.
function getRefreshIntervalSecondsForSlot(slotAd) {
    var currentAdvertiserId = getCurrentAdvertiserIdForSlot(slotAd);

    if (GAM_SLOW_REFRESH_ADVERTISER_IDS.includes(currentAdvertiserId)) {
        return SECONDS_TO_WAIT_FOR_SLOW_REFRESH_ADVERTISERS;
    }

    return SECONDS_TO_WAIT_AFTER_VIEWABILITY;
}


// -----------------------------------------------------------------------------
// GAM auction-quality telemetry - aligned with the existing Žena aq_* keys.
// Values are deliberately bucketed to keep GAM key cardinality low.
// -----------------------------------------------------------------------------

var AUCTION_QUALITY_PREVIOUS_RENDER_BY_DIV_ID = {};

function bucketAuctionQualityCount(count) {
    if (!count || count <= 0) return '0';
    if (count === 1) return '1';
    if (count === 2) return '2';
    if (count === 3) return '3';
    if (count === 4) return '4';
    return '5plus';
}

function bucketAuctionQualityTopBid(cpm) {
    if (!(cpm > 0)) return 'no_bid';
    if (cpm < 0.10) return 'lt_010';
    if (cpm < 0.20) return '010_019';
    if (cpm < 0.50) return '020_049';
    if (cpm < 1.00) return '050_099';
    return '100_plus';
}

function getGptSlotByDivId(divId) {
    var allSlots = googletag.pubads().getSlots();

    for (var slotIndex = 0; slotIndex < allSlots.length; slotIndex++) {
        if (
            allSlots[slotIndex] &&
            allSlots[slotIndex].getSlotElementId &&
            allSlots[slotIndex].getSlotElementId() === divId
        ) {
            return allSlots[slotIndex];
        }
    }

    return null;
}

function applyAuctionQualityTargeting(divId, slotAd, bidsResponse, timedOut) {
    if (!divId || !slotAd || !slotAd.setTargeting) {
        return;
    }

    var adUnitBidResponse = bidsResponse && bidsResponse[divId];
    var bids = adUnitBidResponse && Array.isArray(adUnitBidResponse.bids)
        ? adUnitBidResponse.bids
        : [];

    var uniqueBidders = {};
    var topBidCpm = 0;

    bids.forEach(function (bid) {
        var bidderCode = bid && (bid.bidderCode || bid.bidder);

        if (bidderCode) {
            uniqueBidders[bidderCode] = true;
        }

        if (bid && typeof bid.cpm === 'number' && bid.cpm > topBidCpm) {
            topBidCpm = bid.cpm;
        }
    });

    var previousRender = AUCTION_QUALITY_PREVIOUS_RENDER_BY_DIV_ID[divId] || {
        fill: 'initial',
        source: 'initial'
    };

    slotAd.setTargeting('aq_bid_count', bucketAuctionQualityCount(bids.length));
    slotAd.setTargeting('aq_bidder_count', bucketAuctionQualityCount(Object.keys(uniqueBidders).length));
    slotAd.setTargeting('aq_top_bid_bucket', bucketAuctionQualityTopBid(topBidCpm));
    slotAd.setTargeting('aq_timed_out', timedOut === true ? 'yes' : 'no');
    slotAd.setTargeting('aq_prev_fill', previousRender.fill);
    slotAd.setTargeting('aq_prev_source', previousRender.source);
}

function rememberAuctionQualityRender(event) {
    if (!event || !event.slot || !event.slot.getSlotElementId) {
        return;
    }

    var divId = event.slot.getSlotElementId();

    if (!divId) {
        return;
    }

    if (event.isEmpty === true) {
        AUCTION_QUALITY_PREVIOUS_RENDER_BY_DIV_ID[divId] = {
            fill: 'empty',
            source: 'unfilled'
        };
        return;
    }

    var hbAdId = [];

    try {
        hbAdId = event.slot.getTargeting('hb_adid') || [];
    } catch (error) {
        hbAdId = [];
    }

    AUCTION_QUALITY_PREVIOUS_RENDER_BY_DIV_ID[divId] = {
        fill: 'filled',
        source: hbAdId.length ? 'prebid' : 'gam'
    };
}



var defineSlotSizesForInText = [
  // rectangles
  [200, 200],
  [250, 250],
  [300, 250],
  [336, 280],
  [300, 300],
  [320, 320],
  [300, 350],
  [300, 400],
  [320, 400],
  [250, 360],
  [250, 400],

  // wide (safe for 910px column)
  [580, 280],
  [728, 90],
  [750, 100],
  [750, 200],
  [750, 250],
  [750, 280]
];

var sizeMappingForDesktopForInText = [
  [300, 250],
  [336, 280],
  [250, 250],
  [300, 300],
  [320, 320],
  [300, 400],
  [250, 360],
  [300, 350],
  [250, 400],
  [200, 200],
  [320, 400],
  [728, 90],
  [750, 100],
  [750, 200],
  [750, 250],
  [750, 280],
  [580, 280]
];
var sizeMappingForTabletForInText = [
  [468, 60],
  [300, 250],
  [336, 280],
  [250, 250],
  [300, 300],
  [320, 320],
  [300, 400],
  [250, 360],
  [300, 350],
  [250, 400],
  [200, 200],
  [320, 400]
];
var sizeMappingForMobileForInText = [
  [300, 250],
  [336, 280],
  [250, 250],
  [300, 300],
  [320, 320],
  [300, 400],
  [250, 360],
  [300, 350],
  [250, 400],
  [200, 200],
  [320, 400]
];

var sizeConfigForPrebidForDesktopForInText = [
  [300, 250],
  [336, 280],
  [300, 300],
  [320, 320],
  [300, 400],
  [320, 400],
  [250, 250],
  [200, 200],
  [728, 90],
  [580, 280],
  [750, 100],
  [750, 200]
];
var sizeConfigForPrebidForMobileForInText = [
  [300, 250],
  [336, 280],
  [300, 300],
  [320, 320],
  [300, 400],
  [320, 400],
  [250, 250],
  [200, 200]
];
var sizeConfigForPrebidForTabletForInText = [
  [300, 250],
  [336, 280],
  [300, 300],
  [320, 320],
  [300, 400],
  [320, 400],
  [250, 250],
  [200, 200],
  [468, 60]
];
var compiledSizeConfigForPrebidForIntext = [
    { minViewPort: [1024, 0], sizes: sizeConfigForPrebidForDesktopForInText }, //desktop
    { minViewPort: [469, 0], sizes: sizeConfigForPrebidForTabletForInText }, //tablet
    { minViewPort: [0, 0], sizes: sizeConfigForPrebidForMobileForInText }, //mobile
];


// -----------------------------------------------------------------------------
// Single-pass initialization
// -----------------------------------------------------------------------------
//
// The wrapper starts early on DOMContentLoaded, with window.load kept only as a
// safety fallback if the first path did not run. The previous second pass was
// removed so the same GPT slots do not receive duplicate refresh observers or
// duplicate event listeners.
// -----------------------------------------------------------------------------

// Track which slot divIds we already initialized.
window.__adsInitializedDivIds = window.__adsInitializedDivIds || {};

function runWrapper() {

    var deviceLabels = [];

    if (adWindowWidth > 468) {
        deviceLabels.push("desktop");
        //console.log(deviceLabels);
    }

    else {
        deviceLabels.push("mobile");
        //console.log(deviceLabels);
    }

    //console.log('Ad Wrapper: Script execution.');

    var adContainers = document.querySelectorAll('.wrapperAd'); // Find all divs which have wrapperAd class.

    googletag.cmd.push(function() { // Push the defined ad units to the GPT array.

        // Sticky GPT size mapping.
        var sticky_mapping = googletag.sizeMapping()
        .addSize([1600, 0], [[728, 90], [970, 90], [980, 90], [1000, 100], [750, 100], [750, 200]]) //desktop for bigger screens
        .addSize([1024, 0], [[728, 90]]) //desktop for smaller screens
        .addSize([469, 0], [[468, 60]]) //tablet
        .addSize([0, 0], [[300, 50], [320, 50], [300, 100], [320, 100]]) //mobile
        .build();

        // Billboard GPT size mapping.
        var billboard_mapping = googletag.sizeMapping()
        .addSize([1024, 0], [[728, 90], [970, 66], [950, 90], [970, 90], [980, 90], [1000, 100], [750, 100], [750, 200], [980, 120], [930, 180], [970, 250], [980, 240], [980, 250], [990, 250], [1060, 250]]) //desktop
        .addSize([469, 0], [[468, 60]]) //tablet
        .addSize([0, 0], [[300, 50], [300, 75], [320, 80], [320, 50], [300, 100], [320, 100]]) //mobile
        .build();

        // Branding GPT size mapping.
        var branding_mapping = googletag.sizeMapping()
        .addSize([1900, 0], [[300, 600], [470, 1080]]) //desktop for bigger screens
        .addSize([1600, 0], [[120, 600], [160, 600]]) //desktop for smaller screens
        .addSize([0, 0], []) //tablet & mobile
        .build();

        // P GPT size mapping.
        var p_mapping = googletag.sizeMapping()
        .addSize([1024, 0], [[250, 250], [300, 250]]) //desktop
        .addSize([469, 0], [[250, 250], [300, 250]]) //tablet
        .addSize([0, 0], [[250, 250], [300, 250], [336, 280]]) //mobile
        .build();

        var inText_mapping = googletag.sizeMapping()
        .addSize([1024, 0], sizeMappingForDesktopForInText) //desktop
        .addSize([469, 0], sizeMappingForTabletForInText) //tablet
        .addSize([0, 0], sizeMappingForMobileForInText) //mobile
        .build();

        adContainers.forEach(function(el) {
// Skip already initialized containers (second pass protection).
if (window.__adsInitializedDivIds && window.__adsInitializedDivIds[el.id]) {
    return;
}
if (window.__adsInitializedDivIds) {
    window.__adsInitializedDivIds[el.id] = true;
}

 // Runs through all of the wrapperAd divs.

            // **************** Sticky GPT and Prebid definition. *******************

            if (el.id === 'Adhesion') { // Check if Adhesion div is present in body. 

                // Sticky ad unit definition
                Adhesion = googletag.defineSlot(adUnitPath + 'Sticky', [[728, 90], [970, 90], [320, 50]], 'Adhesion')
                                    .defineSizeMapping(sticky_mapping)
                                    .setTargeting('position', 'Adhesion')
                                    .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                    .addService(googletag.pubads());
                
                // Push the defined Adhesion Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'Adhesion',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1600, 0], sizes: [[728, 90], [970, 90], [750, 100], [750, 200]] }, //desktop for bigger screens
                            { minViewPort: [1024, 0], sizes: [[728, 90]] }, //desktop for smaller screens
                            { minViewPort: [469, 0], sizes: [[468, 60]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100]] }, //mobile
                        ]
                        }},
                        bids: [
                            // Adform (adf) bids are NET: the adapter defaults to gross, every other
                            // Adform entry in this file (incl. the mediapool alias) is net, and GAM
                            // compares hb_pb against net AdX prices (added 05102026, all 12 entries).
                            { bidder: 'adf', params: { mid: '2275332', priceType: 'net' } },
                            { bidder: 'mediapool', params: { mid: '1984550', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070290, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1369 } },
                            { bidder: "ix", params: { siteId: "933893" } },
                            { bidder: "ogury", labelAny: ["mobile"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wm-hb-foot-pulson-ringi-pnwye50remz0" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558760322" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892446" } },
                            { bidder: "richaudience", params: { pid: "kByQH9hxFq", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730852 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120620" } },
                            { bidder: "teads", params: { pageId: 182464, placementId: 197469 } },
                        ]
                    }
                );

            }

            // **************** Billboard GPT and Prebid definition. *******************

            else if (el.id === 'Billboard') { // Check if Billboard div is present in body. 

                // Billboard ad unit definition
                Billboard = googletag.defineSlot(adUnitPath + 'Billboard', [[970, 250]], 'Billboard')
                                     .defineSizeMapping(billboard_mapping)
                                     .setTargeting('position', 'Billboard')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                     .addService(googletag.pubads());
                
                // Push the defined Billboard Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'Billboard',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[970, 90], [728, 90], [970, 250]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[468, 60]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 50], [320, 50], [300, 100], [320, 100]] }, //mobile
                        ]
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275333', priceType: 'net' } },
                            { bidder: 'mediapool', params: { mid: '1984551', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070292, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1370 } },
                            { bidder: "ix", params: { siteId: "933894" } },
                            { bidder: "ogury", labelAny: ["desktop"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wd-hb-stdb-pulson-ringi-rqhnxyhmxkmi" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558760323" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892449" } },
                            { bidder: "richaudience", params: { pid: "pP4Vt4VXKF", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730860 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120621" } },
                            { bidder: "teads", params: { pageId: 182464, placementId: 197469 } },
                        ]
                    }
                );

            }

            // **************** Branding_Left GPT and Prebid definition. *******************

            else if (el.id === 'Branding_Left') { // Check if Branding_Left div is present in body. 

                // Branding_Left ad unit definition
                Branding_Left = googletag.defineSlot(adUnitPath + 'Branding_Left', [[120, 600], [160, 600], [300, 600], [470, 1080]], 'Branding_Left')
                                         .defineSizeMapping(branding_mapping)
                                         .setTargeting('position', 'Branding_Left')
                                         .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                         .addService(googletag.pubads());
                
                // Push the defined Branding_Left Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'Branding_Left',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1900, 0], sizes: [[300, 600]] }, //desktop for bigger screens
                            { minViewPort: [1600, 0], sizes: [[120, 600], [160, 600]] }, //desktop for smaller screens
                            { minViewPort: [0, 0], sizes: [] }, //mobile
                        ]
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275334', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070293, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1371 } },
                            { bidder: "ix", params: { siteId: "934008" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558760325" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892453" } },
                            { bidder: "richaudience", params: { pid: "9tmmn91V0M", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730868 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120622" } },
                            { bidder: "teads", params: { pageId: 182464, placementId: 197469 } },
                        ]
                    }
                );

            }

            // **************** Branding_Right GPT and Prebid definition. *******************

            else if (el.id === 'Branding_Right') { // Check if Branding_Right div is present in body. 

                // Branding_Right ad unit definition
                Branding_Right = googletag.defineSlot(adUnitPath + 'Branding_Right', [[120, 600], [160, 600], [300, 600], [470, 1080]], 'Branding_Right')
                                          .defineSizeMapping(branding_mapping)
                                          .setTargeting('position', 'Branding_Right')
                                          .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                          .addService(googletag.pubads());
                
                // Push the defined Branding_Right Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'Branding_Right',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1900, 0], sizes: [[300, 600]] }, //desktop for bigger screens
                            { minViewPort: [1600, 0], sizes: [[120, 600], [160, 600]] }, //desktop for smaller screens
                            { minViewPort: [0, 0], sizes: [] }, //mobile
                        ]
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275335', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070295, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1372 } },
                            { bidder: "ix", params: { siteId: "934009" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558760326" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892455" } },
                            { bidder: "richaudience", params: { pid: "ArNlbXjPUI", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730872 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120623" } },
                            { bidder: "teads", params: { pageId: 182464, placementId: 197469 } },
                        ]
                    }
                );

            }

            // **************** P1 GPT and Prebid definition. *******************

            else if (el.id === 'P1') { // Check if P1 div is present in body. 

                // P1 ad unit definition
                P1 = googletag.defineSlot(adUnitPath + 'P1', [[300, 250]], 'P1')
                              .defineSizeMapping(p_mapping)
                              .setTargeting('position', 'P1')
                              .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                              .addService(googletag.pubads());
                
                // Push the defined P1 Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'P1',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[300, 250]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[300, 250]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 250], [336, 280]] }, //mobile
                        ]
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275336', priceType: 'net' } },
                            { bidder: 'mediapool', params: { mid: '1984552', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070296, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1373 } },
                            { bidder: "ix", params: { siteId: "934010" } },
                            { bidder: "ogury", labelAny: ["desktop"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wd-hb-stdb-pulson-ringi-zi4dgt7gk6lx" } },
                            { bidder: "ogury", labelAny: ["mobile"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wm-hb-iart-pulson-ringi-f0cm794x56co" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558760327" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892456" } },
                            { bidder: "richaudience", params: { pid: "fIr1aJByIS", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730874 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120624" } },
                            { bidder: "teads", params: { pageId: 182464, placementId: 197469 } },
                        ]
                    }
                );

            }

            // **************** P2 GPT and Prebid definition. *******************

            else if (el.id === 'P2') { // Check if P2 div is present in body. 

                // P2 ad unit definition
                P2 = googletag.defineSlot(adUnitPath + 'P2', [[300, 250]], 'P2')
                              .defineSizeMapping(p_mapping)
                              .setTargeting('position', 'P2')
                              .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                              .addService(googletag.pubads());
                
                // Push the defined P2 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'P2',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[300, 250]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[300, 250]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 250], [336, 280]] }, //mobile
                        ]
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275344', priceType: 'net' } },
                            { bidder: 'mediapool', params: { mid: '1984552', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070297, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1374 } },
                            { bidder: "ix", params: { siteId: "934011" } },
                            { bidder: "ogury", labelAny: ["desktop"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wd-hb-stdb-pulson-ringi-zyfwio0vywbp" } },
                            { bidder: "ogury", labelAny: ["mobile"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wm-hb-iart-pulson-ringi-f0cm794x56co" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558760328" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892457" } },
                            { bidder: "richaudience", params: { pid: "veWe11u4Yx", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730876 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120625" } },
                            { bidder: "teads", params: { pageId: 182464, placementId: 197469 } },
                        ]
                    }
                );

            }

            // **************** P3 GPT and Prebid definition. *******************

            else if (el.id === 'P3') { // Check if P3 div is present in body. 

                // P3 ad unit definition
                P3 = googletag.defineSlot(adUnitPath + 'P3', [[300, 250]], 'P3')
                              .defineSizeMapping(p_mapping)
                              .setTargeting('position', 'P3')
                              .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                              .addService(googletag.pubads());
                
                // Push the defined P3 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'P3',
                        mediaTypes: {banner: {
                        sizeConfig: [
                            { minViewPort: [1024, 0], sizes: [[300, 250]] }, //desktop
                            { minViewPort: [469, 0], sizes: [[300, 250]] }, //tablet
                            { minViewPort: [0, 0], sizes: [[300, 250], [336, 280]] }, //mobile
                        ]
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275343', priceType: 'net' } },
                            { bidder: 'mediapool', params: { mid: '1984552', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070298, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1375 } },
                            { bidder: "ix", params: { siteId: "934012" } },
                            { bidder: "ogury", labelAny: ["desktop"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wd-hb-stdb-pulson-ringi-wqrp2y53ulth" } },
                            { bidder: "ogury", labelAny: ["mobile"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wm-hb-iart-pulson-ringi-f0cm794x56co" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558760329" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892458" } },
                            { bidder: "richaudience", params: { pid: "C6m38k3SEX", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730880 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120626" } },
                            { bidder: "teads", params: { pageId: 182464, placementId: 197469 } },
                        ]
                    }
                );

            }   

            // **************** In_Text_1 GPT and Prebid definition. *******************

            else if (el.id === 'In_Text_1') { // Check if In_Text_1 div is present in body. 

                // In_Text_1 ad unit definition
                In_Text_1 = googletag.defineSlot(adUnitPath + 'In_Text_1', defineSlotSizesForInText, 'In_Text_1')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'In_Text_1')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                     .addService(googletag.pubads());
                
                // Push the defined In_Text_1 Prebid placement IDs to the ATF array.      
                adUnitsATF.push(
                    {   code: 'In_Text_1',
                        mediaTypes: {banner: {
                        sizeConfig: compiledSizeConfigForPrebidForIntext
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275342', priceType: 'net' } },
                            { bidder: 'mediapool', params: { mid: '1984553', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070299, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1376 } },
                            { bidder: "ix", params: { siteId: "935040" } },
                            { bidder: "ogury", labelAny: ["desktop"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wd-hb-stdb-pulson-ringi-c0gfbph6jl77" } },
                            { bidder: "ogury", labelAny: ["mobile"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wm-hb-iart-pulson-ringi-f0cm794x56co" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558767918" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892459" } },
                            { bidder: "richaudience", params: { pid: "Ufj5hvH1Pz", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730882 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120627" } },
                            { bidder: "teads", params: { pageId: 182465, placementId: 197470 } },
                        ]
                    }
                );

            }   

            // **************** In_Text_2 GPT and Prebid definition. *******************

            else if (el.id === 'In_Text_2') { // Check if In_Text_2 div is present in body. 

                // In_Text_2 ad unit definition
                In_Text_2 = googletag.defineSlot(adUnitPath + 'In_Text_2', defineSlotSizesForInText, 'In_Text_2')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'In_Text_2')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                     .addService(googletag.pubads());
                
                // Push the defined In_Text_2 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'In_Text_2',
                        mediaTypes: {banner: {
                        sizeConfig: compiledSizeConfigForPrebidForIntext
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275341', priceType: 'net' } },
                            { bidder: 'mediapool', params: { mid: '1984553', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070300, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1377 } },
                            { bidder: "ix", params: { siteId: "935041" } },
                            { bidder: "ogury", labelAny: ["desktop"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wd-hb-stdb-pulson-ringi-6lcturvfqvzn" } },
                            { bidder: "ogury", labelAny: ["mobile"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wm-hb-iart-pulson-ringi-f0cm794x56co" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558767919" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892460" } },
                            { bidder: "richaudience", params: { pid: "jdLFsMqKD1", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730884 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120628" } },
                            { bidder: "teads", params: { pageId: 182465, placementId: 197470 } },
                        ]
                    }
                );

            }   

            // **************** In_Text_3 GPT and Prebid definition. *******************

            else if (el.id === 'In_Text_3') { // Check if In_Text_3 div is present in body. 

                // In_Text_3 ad unit definition
                In_Text_3 = googletag.defineSlot(adUnitPath + 'In_Text_3', defineSlotSizesForInText, 'In_Text_3')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'In_Text_3')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                     .addService(googletag.pubads());
                
                // Push the defined In_Text_3 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'In_Text_3',
                        mediaTypes: {banner: {
                        sizeConfig: compiledSizeConfigForPrebidForIntext
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275340', priceType: 'net' } },
                            { bidder: 'mediapool', params: { mid: '1984553', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070301, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1378 } },
                            { bidder: "ix", params: { siteId: "935042" } },
                            { bidder: "ogury", labelAny: ["desktop"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wd-hb-stdb-pulson-ringi-b23p08phg033" } },
                            { bidder: "ogury", labelAny: ["mobile"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wm-hb-iart-pulson-ringi-f0cm794x56co" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "558767920" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "4892461" } },
                            { bidder: "richaudience", params: { pid: "Uq89kWJ2lr", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2730890 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "120629" } },
                            { bidder: "teads", params: { pageId: 182465, placementId: 197470 } },
                        ]
                    }
                );

            }  

            // **************** In_Text_4 GPT and Prebid definition. *******************

            else if (el.id === 'In_Text_4') { // Check if In_Text_4 div is present in body. 

                // In_Text_4 ad unit definition
                In_Text_4 = googletag.defineSlot(adUnitPath + 'In_Text_4', defineSlotSizesForInText, 'In_Text_4')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'In_Text_4')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                     .addService(googletag.pubads());
                
                // Push the defined In_Text_4 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'In_Text_4',
                        mediaTypes: {banner: {
                        sizeConfig: compiledSizeConfigForPrebidForIntext
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275339', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070302, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1379 } },
                            { bidder: "ix", params: { siteId: "1008657" } },
                            { bidder: "ogury", labelAny: ["desktop"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wd-hb-stdb-pulson-ringi-m5btcrasved7" } },
                            { bidder: "ogury", labelAny: ["mobile"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wm-hb-iart-pulson-ringi-f0cm794x56co" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559599741" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5280467" } },
                            { bidder: "richaudience", params: { pid: "3bfXHN38T6", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2961436 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "124582" } },
                            { bidder: "teads", params: { pageId: 182465, placementId: 197470 } },
                        ]
                    }
                );

            }  

            // **************** In_Text_5 GPT and Prebid definition. *******************

            else if (el.id === 'In_Text_5') { // Check if In_Text_5 div is present in body. 

                // In_Text_5 ad unit definition
                In_Text_5 = googletag.defineSlot(adUnitPath + 'In_Text_5', defineSlotSizesForInText, 'In_Text_5')
                                     .defineSizeMapping(inText_mapping)
                                     .setTargeting('position', 'In_Text_5')
                                     .setTargeting('refresh_count', '0')
                                    .setTargeting('refresh_bucket', 'initial')
                                     .addService(googletag.pubads());
                
                // Push the defined In_Text_5 Prebid placement IDs to the BTF array.      
                adUnitsBTF.push(
                    {   code: 'In_Text_5',
                        mediaTypes: {banner: {
                        sizeConfig: compiledSizeConfigForPrebidForIntext
                        }},
                        bids: [
                            { bidder: 'adf', params: { mid: '2275338', priceType: 'net' } },
                            // { bidder: "appnexus", params: { placementId: 34070303, usePaymentRule: true } },
                            //{ bidder: "connectad", params: { networkId: 44, siteId: 1053638 } },
                            { bidder: "eskimi", params: { placementId: 1380 } },
                            { bidder: "ix", params: { siteId: "1008658" } },
                            { bidder: "ogury", labelAny: ["desktop"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wd-hb-stdb-pulson-ringi-ree7zzwqhpgy" } },
                            { bidder: "ogury", labelAny: ["mobile"], params: { assetKey: "OGY-7FC4A7F19778", adUnitId: "wm-hb-iart-pulson-ringi-f0cm794x56co" } },
                            { bidder: "openx", params: { delDomain: "ringieraxelspr-d.openx.net", unit: "559599742" } },
                            { bidder: "pubmatic", params: { publisherId: "158892", adSlot: "5280468" } },
                            { bidder: "richaudience", params: { pid: "DkiVpmmz3S", supplyType: "site" } },
                            { bidder: "rtbhouse", params: { region: "prebid-eu", publisherId: "eq7oNcAuPBmB2fDVrcVE", bidfloor: 0.10 } },
                            { bidder: "magnite", params: { accountId: 19712, siteId: 464372, zoneId: 2961440 } },
                            // Bidder code must match the compiled adapter name
                            // ("smartadserverBidAdapter") in the deployed Prebid library.
                            { bidder: "smartadserver", params: { networkId: "4527", siteId: "582598", pageId: "1769348", formatId: "124583" } },
                            { bidder: "teads", params: { pageId: 182465, placementId: 197470 } },
                        ]
                    }
                );

            }  
        }); // End of wrapperAd divs check. 

        //Create sticky ad once the ad is ready to render
        googletag.pubads().addEventListener('slotRenderEnded', function(event) {

            rememberAuctionQualityRender(event);

            let unitPath = event.slot.getAdUnitPath(); //get the ad unith path for the slot which will render the ad
            let stickyBackground = document.getElementById('adhesion_background'); //get the adhesion background id
            //let windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth; //check user's screen width

            if (unitPath === adUnitPath + "Sticky" && event.isEmpty === true) {
                let sticky = document.getElementById('Adhesion');
                sticky.style.display = 'none';
            }
            else if (unitPath === adUnitPath + "Sticky" && window.adhesionDisabled === true) {
                let sticky = document.getElementById('Adhesion');
                if (sticky) {
                    sticky.style.display = 'none';
                }
            }

            // if ad unit is sticky and the background is not set, set sticky CSS for sticky ad unit
            else if (unitPath === adUnitPath + "Sticky" && stickyBackground === null){
            let sticky = document.getElementById('Adhesion');
            sticky.style.position = 'fixed';
            sticky.style.zIndex = '999999';
            sticky.style.width = '100%';
            sticky.style.height = '';
            sticky.style.bottom = '0px';
            sticky.style.textAlign = 'center';
            sticky.style.padding = '10px 0px 0px';
            sticky.style.borderTop = '1px #CCC solid';

            // create adhesion background
            let background = document.createElement("div");
            background.setAttribute("ID", "adhesion_background");
            background.style.backgroundColor = '#FFF';

            if (adWindowWidth > 1024) {
                background.style.opacity = '70%';
            }

            else {
                background.style.opacity = '100%';
            }

            background.style.position = 'absolute';
            background.style.height = '100%';
            background.style.width = '100%';
            background.style.top = '0';
            background.style.left = '0';
            background.style.zIndex = '-1';
            sticky.appendChild(background);

            // create placeholder text
            /*let placeholder = document.createElement("div");
            placeholder.setAttribute("ID", "adText");
            placeholder.style.textAlign = 'center';
            placeholder.style.fontSize = '10px';
            placeholder.style.color = '#CCC';
            placeholder.style.height = '20px';
            placeholder.style.borderTop = '1px #CCC solid';
            placeholder.style.paddingTop = '2px';
            placeholder.textContent = 'Oglas'
            background.appendChild(placeholder);*/

            // create close button with svg elements
            let closeButton = document.createElement("div");
            closeButton.setAttribute("ID", "close_adhesion")
            closeButton.style.position = 'absolute';
            closeButton.style.top = '-29px';
            closeButton.style.right = '0px';
            closeButton.style.cursor = 'pointer';
            closeButton.style.padding = '8px';
            //closeButton.style.borderRadius = '50%';
            closeButton.style.border = '1px solid #CCC';
            //closeButton.style.boxShadow = '0 0 3px #b8b9be';
            closeButton.style.background = '#FFF';
            closeButton.style.borderBottom = '1px solid #FFF';
            closeButton.style.borderTopLeftRadius = '10px';

            let iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            let iconPath = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'path'
            );

            iconSvg.setAttribute('display', 'block');
            iconSvg.setAttribute('fill', '#000');
            iconSvg.setAttribute('width', '12px');
            iconSvg.setAttribute('height', '12px');
            iconSvg.setAttribute('viewBox', '0 0 8 8');

            iconPath.setAttribute(
                'd',
                'M1.41 0l-1.41 1.41.72.72 1.78 1.81-1.78 1.78-.72.69 1.41 1.44.72-.72 1.81-1.81 1.78 1.81.69.72 1.44-1.44-.72-.69-1.81-1.78 1.81-1.81.72-.72-1.44-1.41-.69.72-1.78 1.78-1.81-1.78-.72-.72z'
            );

            iconSvg.appendChild(iconPath);
            closeButton.appendChild(iconSvg);
            sticky.appendChild(closeButton);

            // when user clicks the close button:
            // - 1st close: hide and auto-show again after 10s, but only when page is visible
            // - 2nd close: permanently hide for the rest of the page view
            // (if user clicks close again before the 10s passes, restart the timer)
            document.getElementById("close_adhesion").addEventListener('click', function(e) {
                e.preventDefault();

                // Track how many times the user closed the adhesion during this page view
                window.adhesionCloseCount = (window.adhesionCloseCount || 0) + 1;

                // Always hide immediately
                sticky.style.display = 'none';

                // Clear any previous reopen timer
                if (window.adhesionReopenTimer) {
                    clearTimeout(window.adhesionReopenTimer);
                    window.adhesionReopenTimer = null;
                }

                // If this is the 2nd close, permanently disable it for the remainder of the page view
                if (window.adhesionCloseCount >= 2) {
                    window.adhesionDisabled = true;

                    // Remove any pending visibility listener
                    if (window.adhesionVisibilityListener) {
                        document.removeEventListener('visibilitychange', window.adhesionVisibilityListener);
                        window.adhesionVisibilityListener = null;
                    }

                    return;
                }

                // Helper: show only if not disabled and page is visible
                function showIfAllowed() {
                    if (window.adhesionDisabled === true) return;

                    if (document.visibilityState === 'visible') {
                        sticky.style.display = 'block';

                        // Clean up any visibility listener if we used one
                        if (window.adhesionVisibilityListener) {
                            document.removeEventListener('visibilitychange', window.adhesionVisibilityListener);
                            window.adhesionVisibilityListener = null;
                        }
                    } else {
                        // If tab is hidden, wait until it becomes visible
                        if (!window.adhesionVisibilityListener) {
                            window.adhesionVisibilityListener = function() {
                                if (document.visibilityState === 'visible') {
                                    showIfAllowed();
                                }
                            };
                            document.addEventListener('visibilitychange', window.adhesionVisibilityListener);
                        }
                    }
                }

                // Re-show after 10 seconds (or later if the page is hidden)
                window.adhesionReopenTimer = setTimeout(function() {
                    window.adhesionReopenTimer = null;
                    showIfAllowed();
                }, 10000);
            }, false);


            } 
        });

        // **************** Web interstitial GPT definition (11092026 variant B). *******************
        //
        // Defined outside the adContainers loop on purpose: there is no ".wrapperAd" div for
        // it, GPT renders the interstitial in its own full-screen container. Defined for every
        // device width; GPT decides eligibility itself and returns null when the format is not
        // allowed in this context, in which case the wrapper simply carries on without it.

        if (googletag.enums && googletag.enums.OutOfPageFormat && googletag.enums.OutOfPageFormat.INTERSTITIAL !== undefined) {

            Interstitial = googletag.defineOutOfPageSlot(adUnitPath + 'Interstitial', googletag.enums.OutOfPageFormat.INTERSTITIAL);

            if (Interstitial) {
                Interstitial.setTargeting('position', 'Interstitial')
                            .setTargeting('refresh_count', '0')
                            .setTargeting('refresh_bucket', 'initial')
                            .addService(googletag.pubads());

                adsDebugLog('[ads-interstitial] GPT web interstitial slot defined', {
                    adUnitPath: adUnitPath + 'Interstitial',
                    slotElementId: Interstitial.getSlotElementId()
                });
            } else {
                adsDebugLog('[ads-interstitial] GPT refused the interstitial slot in this context (null returned).');
            }
        }

        // Feature detect temporary Privacy Sandbox API first
        if ('cookieDeprecationLabel' in navigator) {
            // Request value and resolve promise
            navigator.cookieDeprecationLabel.getValue().then((label) => {
                googletag.pubads().setTargeting("privacyLabel", label);
            });
        }
        
        // Check if adsKeywords object is present
        if (typeof adsKeywords != 'undefined') {

            // Check if adsKeywords attributes are present, if so, pass them as key values to GAM
            if (typeof adsKeywords.category != 'undefined') {
                googletag.pubads().setTargeting('category', adsKeywords.category);
            } 

            if (typeof adsKeywords.pageType != 'undefined') {
                googletag.pubads().setTargeting('pageType', adsKeywords.pageType);
            } 

            if (typeof adsKeywords.articleUuid != 'undefined') {
                googletag.pubads().setTargeting('articleId', [adsKeywords.articleUuid].join(','));
            } 
            
            if (typeof adsKeywords.articleTags != 'undefined') {
                googletag.pubads().setTargeting('articleTags', [adsKeywords.articleTags].join(','));
            } 
        }

        let adQueryString = window.location.search; // Get the whole string in the URL. 
        //console.log("Query string is " + adQueryString);

        let adUrlParams = new URLSearchParams(adQueryString); // Get the information after ? in the URL. 
        //console.log("URL param is " +  adUrlParams);

        if (adUrlParams.has('adTest')) { 
            let adValue = adUrlParams.get('adTest'); // If there is a adTest param get the value after =. 
            //console.log("AdTest value is " + adValue);
            googletag.pubads().setTargeting('adTest', adValue); // Send the adTest value to GAM.
        } 

        if (adUrlParams.has('utm_source')) {
            let utmSource = adUrlParams.get('utm_source'); // If there is a utm_source param get the value after =. 
            //console.log("UTM source value is " + utmSource);
            googletag.pubads().setTargeting('utmSource', utmSource); // Send the utm_source value to GAM.
        } 

        if (adUrlParams.has('utm_medium')) {
            let utmMedium = adUrlParams.get('utm_medium'); // If there is a utm_medium param get the value after =. 
            //console.log("UTM medium value is " + utmMedium);
            googletag.pubads().setTargeting('utmMedium', utmMedium); // Send the utm_medium value to GAM.
        } 

        let refURL = document.referrer; // Get the URL from which the user came. 

        if (refURL.length === 0) {
            googletag.pubads().setTargeting('referrer', 'direct');
        } 

        else if (refURL.includes("google")) {
            googletag.pubads().setTargeting('referrer', 'google');
        } 
          
        else if (refURL.includes("linkedin") || refURL.includes("t.com") || refURL.includes("facebook") || refURL.includes("instagram")) {
            googletag.pubads().setTargeting('referrer', 'social');
        } 
          
        else if (refURL.includes("blic.rs") || refURL.includes("pulsonline.rs") || refURL.includes("ana.rs") || refURL.includes("nekretnine.rs") || refURL.includes("mojauto.rs")) {
            googletag.pubads().setTargeting('referrer', 'ringier');
        } 
          
        else {
            googletag.pubads().setTargeting('referrer', 'other');
        }

        googletag.pubads().disableInitialLoad(); // Disables requests for ads on page load. 
        googletag.pubads().enableSingleRequest(); // Enables single request mode for fetching multiple ads at the same time.
        googletag.pubads().setCentering(true);
        googletag.enableServices(); // Enables all GPT services that have been defined for ad slots on the page.

        // Get the list of slots associated with this service.
        googletag.pubads().getSlots().forEach(function(adSlots) {
            // Out-of-page slots (the web interstitial) have no div in the page and must be
            // handed to display() as the slot object itself; ordinary slots by element id.
            if (adSlots.getOutOfPage && adSlots.getOutOfPage()) {
                googletag.display(adSlots);
                return;
            }
            googletag.display(adSlots.getSlotElementId()); // Instructs slot services to render all of the defined ad slots. 
        });

        // IMPORTANT: initialize Prebid only after GPT slots and the adUnitsATF/adUnitsBTF
        // arrays have been populated. googletag.cmd and pbjs.que are independent async
        // queues, so starting Prebid outside this block can make pbjs.addAdUnits() run
        // with empty arrays, causing no bid responses and no hb_* targeting in GAM.
        initializePrebidAfterSlotDefinitions();

    });

    function initializePrebidAfterSlotDefinitions() {
        if (window.__pulsonlinePrebidInitialized) {
            return;
        }

        window.__pulsonlinePrebidInitialized = true;

        pbjs.que.push(function() {

        pbjs.aliasBidder('adf', 'mediapool', {gvlid:50});
    
        pbjs.setConfig({
    
            priceGranularity: 'high', //$0.01 increments, up to $20

            enableSendAllBids: false, //send only top bids
            targetingControls: {
              alwaysIncludeDeals: true //send all deal bids
            },
    
            //Currency module
            currency: {
                "adServerCurrency": "EUR",
                "conversionRateFile": "https://cdn.jsdelivr.net/gh/prebid/currency-file@1/latest.json",
             },
    
            //Cookie match and user syncs
            userSync: {
                syncEnabled: true,
                syncsPerBidder: 3,
                syncDelay: 3000,

                // How long Prebid may hold an auction while the user-id
                // submodules below (sharedId, id5Id through its external
                // module, teadsId) are still resolving. Prebid only delays an
                // auction while an id is actually pending: a visitor whose ids
                // are already in storage waits 0 ms, a cold visitor waits at
                // most this long. Raised from 50 to 300 ms in the 05102026
                // build because the consent fast path answers the TCF question
                // at script execution, so the first ATF auction can be
                // requested well before those submodules have fetched their
                // ids, and bidders that rely on them bid less without them.
                // The 02-05.10.2026 read of the arm whose first auction left at
                // script execution showed first-screen Prebid EUR per 1,000
                // served 9 % to 16 % below the arm that auctioned later; this
                // cap keeps the fast path's speed (the CMP used to cost ~1.5 s)
                // while giving the ids a bounded head start.
                auctionDelay: 300,
                userIds: [
    
                    { name: "sharedId", 
                      storage: { type: "cookie", name: "_sharedid", expires: 365 } 
                    },
    
                    { name: "id5Id",
                      params: { partner: 1355, externalModuleUrl: 'https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js' },
                      storage: { type: "html5", name: "id5id", expires: 90, refreshInSeconds: 8*3600 }
                    },
    
                    { name: 'teadsId',
                      params: { pubId: 26849 }
                    },
                    // { name: 'lotamePanoramaId',
                    // },
    
                ],
                aliasSyncEnabled: true,
                filterSettings: { all: { bidders: "*", filter: "include" } },
                topics: {
                    maxTopicCaller: 3,
                    bidders: [

                        {
                            bidder: 'rtbhouse',
                            iframeURL: 'https://topics.authorizedvault.com/topicsapi.html'
                        },

                        {
                            bidder: 'pubmatic',
                            iframeURL: 'https://ads.pubmatic.com/AdServer/js/topics/topics_frame.html',
                        },

                        {
                            bidder: 'openx',
                            iframeURL: 'https://pa.openx.net/topics_frame.html',
                        },

                    ]
                }
            },

            enableTIDs: true,

            consentManagement: {
                gdpr: {
                  cmpApi: 'iab',
                  timeout: 8000,
                  defaultGdprScope: true
                }
            },

            // paapi: {
                // enabled: true,
                // gpt: {
                    // autoconfig: false
                // },
                // defaultForSlots: 1
            // },
            
            // A/B 22092026 arm B (user request): reuse still-valid losing bids in later auctions of the
            // same ad unit. Default in Prebid is false: a bid that loses an auction is thrown away even
            // though the bidder committed to it for its TTL (most adapters declare 300-360 s, a few 55-60 s).
            // With true, Prebid keeps every unrendered, unexpired bid in its cache and lets it compete in
            // the next auction of that ad unit alongside the fresh bids; once a bid wins and renders it is
            // consumed, and an expired bid is dropped. Why here: Pulsonline refreshes every 10 s and 55-80%
            // of refresh auctions return zero fresh bids, and the alternative to a Prebid bid is the house
            // line item at zero real money (Aug 2026 read: eCPM scales 3.3x with bid depth). A bid from the
            // initial auction that lost to AdX or a direct campaign is real demand that today never gets a
            // second chance. Arm A (PROD) keeps the default (false). No bidCacheFilterFunction: every Prebid
            // ad unit on this site is banner-only, so there are no video bids to exclude.
            useBidCache: true,
            
        });

        pbjs.bidderSettings = {
            standard: {
                storageAllowed: true,
            },
        }; 
        
        
        var allPrebidAdUnits = adUnitsATF.concat(adUnitsBTF);
        var atfAdUnitCodes = adUnitsATF.map(function(unit) { return unit.code; });

        if (!allPrebidAdUnits.length || !atfAdUnitCodes.length) {
            window.__pulsonlinePrebidInitialized = false;

            if (typeof adsDebugLog === 'function') {
                adsDebugLog('Pulsonline ads: Prebid initialization skipped because ad unit arrays are empty.', {
                    adUnitsATF: adUnitsATF,
                    adUnitsBTF: adUnitsBTF
                });
            }

            return;
        }

        // Add ATF and BTF ad units only after both arrays are populated.
        // Initial auction requests ATF only; BTF units stay registered for lazy auctions.
        pbjs.addAdUnits(allPrebidAdUnits);
        
        // Request initial ATF bids. Explicit adUnitCodes prevents an accidental all-unit
        // auction while still keeping BTF units registered for later lazy loading.
        // Telemetry (25092026): when the first Prebid auction of the page was dispatched.
        recordFirstRequestMilestone('firstRequestBidsMs');

        pbjs.requestBids({
            labels: deviceLabels,
            adUnitCodes: atfAdUnitCodes,
            bidsBackHandler: function(bidsResponse, timedOut) {
                initAdserver(bidsResponse, timedOut);
            },
            timeout: PREBID_TIMEOUT
        });
    });
    }

    // Send requests to the SSPs and send bids to GAM for the ATF slots.
    function initAdserver(bidsResponse, timedOut) {
        if (pbjs.initAdserverSet) return;
        pbjs.initAdserverSet = true;
        googletag.cmd.push(function() {
            pbjs.que.push(function() {

                /* 
                
                let ppid = pbjs.getUserIds();
                googletag.pubads().setPublisherProvidedId(ppid.pubcid); 
                
                */

                // PPID is optional and must never block the initial ad request.
                // Resolve it asynchronously for subsequent requests when available.
                if (pbjs.getUserIdsAsync) {
                    pbjs.getUserIdsAsync().then(function () {
                        var ppid;

                        try {
                            var storedId5 = window.localStorage.getItem('id5id');
                            var parsedId5 = storedId5
                                ? JSON.parse(decodeURIComponent(storedId5))
                                : null;

                            ppid = parsedId5 && parsedId5.gp;

                            if (ppid) {
                                googletag.pubads().setPublisherProvidedId(ppid);
                            }
                        } catch (error) {
                            // Optional identity data must never affect ad serving.
                        }
                    }).catch(function () {
                        // Optional identity data must never affect ad serving.
                    });
                }

                // Apply AQ targeting using the globally populated ATF ad-unit array.
                // atfAdUnitCodes is local to initializePrebidAuctions() and cannot be
                // referenced here.
                adUnitsATF.forEach(function (adUnit) {
                    if (!adUnit || !adUnit.code) {
                        return;
                    }

                    applyAuctionQualityTargeting(
                        adUnit.code,
                        getGptSlotByDivId(adUnit.code),
                        bidsResponse,
                        timedOut
                    );
                });

                pbjs.setTargetingForGPTAsync();
                // pbjs.setPAAPIConfigForGPT();

                var initialSlots = [
                    Adhesion,
                    Billboard,
                    Branding_Left,
                    Branding_Right,
                    P1,
                    In_Text_1,
                    Interstitial // 11092026 variant B: requested in the same SRA call, no Prebid auction behind it.
                ].filter(function (slot) {
                    return !!slot;
                });

                if (initialSlots.length) {
                    // Telemetry (25092026): page-level timing key-values must be set
                    // before the first ad request leaves the page.
                    recordFirstRequestTelemetryBeforeRefresh('ATF');
                    googletag.pubads().refresh(initialSlots);
                }



            });
        });
    };

    // Use IntersectionObserver API to check whether an ad slot is at the certain distance. 
    var observer = new IntersectionObserver (function(entries) {

        entries.forEach(entry => {

            if (entry.isIntersecting) {

                let adSlot = entry.target.id; // Get the div id of the slot being observed. 

                // Request bids and push the ads to GAM. 
                pbjs.que.push(function() {
                    pbjs.requestBids({
                        labels: deviceLabels,
                        timeout: BTF_PREBID_TIMEOUT,
                        adUnitCodes: [adSlot],
                        bidsBackHandler: function(bidsResponse, timedOut) {
                            applyAuctionQualityTargeting(
                                adSlot,
                                getGptSlotByDivId(adSlot),
                                bidsResponse,
                                timedOut
                            );
                            pbjs.setTargetingForGPTAsync([adSlot]);

                            // Telemetry (25092026): first request if nothing was requested yet
                            // (should not happen), otherwise upgrades a pending consent-wait value.
                            recordFirstRequestTelemetryBeforeRefresh('lazy:' + adSlot);
    
                            // If div with the certain ID is present and observed, refresh GAM slot.
                            if (adSlot === 'P2') {
                                googletag.pubads().refresh([P2]); 
                            } 
        
                            else if (adSlot === 'P3') {
                                googletag.pubads().refresh([P3]); 
                            } 

                            else if (adSlot === 'In_Text_2') {
                                googletag.pubads().refresh([In_Text_2]); 
                            } 

                            else if (adSlot === 'In_Text_3') {
                                googletag.pubads().refresh([In_Text_3]); 
                            } 

                            else if (adSlot === 'In_Text_4') {
                                googletag.pubads().refresh([In_Text_4]); 
                            } 

                            else if (adSlot === 'In_Text_5') {
                                googletag.pubads().refresh([In_Text_5]); 
                            } 

                        }
                    });
                });
                observer.unobserve(entry.target); // Stop observing the ad slot which has alredy been seen. 
            }
        })
    }, {rootMargin: DISTANCE_IN_PIXELS_FOR_LAZY_LOADING}); // Distance to the ad slot.


    document.querySelectorAll('.lazyAd').forEach(function (lazySlotElement) {
    if (!lazySlotElement || !lazySlotElement.getAttribute) {
        return;
    }
    if (lazySlotElement.getAttribute('data-ads-lazy-observed') === '1') {
        return;
    }
    lazySlotElement.setAttribute('data-ads-lazy-observed', '1');
    observer.observe(lazySlotElement);
});
// Check all divs with the class lazyAd and observe them all. 

    // Refresh setup must run after GPT has defined slots; run it inside googletag.cmd.
    googletag.cmd.push(function () {
    // Get the list of slots associated with this service for the refresh logic.
        googletag.pubads().getSlots().forEach(function(slotAd) {

            // 11092026 variant B: the web interstitial is an out-of-page slot. It is never
            // refreshed, it has no div to observe, and its auto-generated element id contains
            // "/" characters that would make the "#" + divId selector below throw. Skip it
            // before anything else in this loop touches it.
            if (slotAd.getOutOfPage && slotAd.getOutOfPage()) {
                return;
            }

            var divId = slotAd.getSlotElementId(); // Get the ID of the ad slot in question.

            if (typeof divId !== 'string' || divId.indexOf('/') !== -1) {
                return; // Not a page div id -- nothing to observe, nothing to refresh.
            }

            var maxRefreshCountForSlot = null; // No refresh cap for any refreshable slot.
            let param = slotAd.getTargeting('refresh_count'); // get the ad slot's refresh_count value (it should be 0 at the start)
            let interval;


            // Use IntersectionObserver API to check whether an ad slot is viewable for certain percent. 
            /**
     * Viewability-based refresh (fixed)
     *
     * Why this exists:
     * - IntersectionObserverEntry.isIntersecting only means ">0% visible", not "X% viewable".
     * - We must use intersectionRatio >= VISIBILITY_PERCENT_FOR_REFRESH.
     * - We run a Prebid auction before every GPT refresh to avoid "GPT-only" refreshes.
     *
     * Behaviour intentionally preserved:
     * - Uses SECONDS_TO_WAIT_AFTER_VIEWABILITY (10s on pulsonline.rs) as the default refresh cadence.
     * - Keeps the existing no-refresh blocklists (advertiser IDs, ad unit IDs).
     * - Does NOT change the Adhesion close/reopen behaviour.
     *
     * New in this release:
     * - Advertisers listed in GAM_SLOW_REFRESH_ADVERTISER_IDS get a slower
     *   SECONDS_TO_WAIT_FOR_SLOW_REFRESH_ADVERTISERS cadence instead of the
     *   default, decided fresh on every refresh cycle via
     *   getRefreshIntervalSecondsForSlot(slotAd). Applies on every ad position;
     *   this is a slowdown, not a block -- for a full block use
     *   GAM_NOREFRESH_ADVERTISER_IDS instead.
     */
    var refreshState = {
        isAboveThreshold: false,
        lastIntersectionRatio: 0,
        aboveThresholdSinceTimestamp: null,
        scheduledRefreshTimeoutId: null,
        currentRefreshCount: 0
    };


    /**
     * Keep a simple debug snapshot per slot when debug mode is enabled.
     */
    function updateRefreshDebugState(partialState) {
        if (!ADS_DEBUG_MODE_ENABLED) {
            return;
        }

        if (!window.__adsRefreshDebugStates) {
            window.__adsRefreshDebugStates = {};
        }

        if (!window.__adsRefreshDebugStates[divId]) {
            window.__adsRefreshDebugStates[divId] = { divId: divId };
        }

        Object.keys(partialState || {}).forEach(function (key) {
            window.__adsRefreshDebugStates[divId][key] = partialState[key];
        });
    }

    updateRefreshDebugState({
        isAboveThreshold: refreshState.isAboveThreshold,
        lastIntersectionRatio: refreshState.lastIntersectionRatio,
        aboveThresholdSinceTimestamp: refreshState.aboveThresholdSinceTimestamp,
        scheduledRefreshTimeoutId: refreshState.scheduledRefreshTimeoutId,
        currentRefreshCount: refreshState.currentRefreshCount,
        refreshBucket: 'initial',
        maxRefreshCount: maxRefreshCountForSlot
    });



    function clearScheduledRefreshWindow() {
        if (refreshState.scheduledRefreshTimeoutId !== null) {
            clearTimeout(refreshState.scheduledRefreshTimeoutId);
            refreshState.scheduledRefreshTimeoutId = null;

            updateRefreshDebugState({
                scheduledRefreshTimeoutId: refreshState.scheduledRefreshTimeoutId,
                lastEvent: "clear_scheduled_window"
            });

            adsDebugLog("[ads-refresh]", divId, "cleared scheduled refresh window");
        }
    }

    function scheduleRefreshWindowIfEligible() {
        if (refreshState.scheduledRefreshTimeoutId !== null) {
            updateRefreshDebugState({ lastSkipReason: "already_scheduled" });
            return;
        }

        if (document.visibilityState !== "visible") {
            updateRefreshDebugState({ lastSkipReason: "tab_hidden" });
            return;
        }

        if (!refreshState.isAboveThreshold) {
            updateRefreshDebugState({ lastSkipReason: "below_threshold" });
            return;
        }

        if (maxRefreshCountForSlot !== null && refreshState.currentRefreshCount >= maxRefreshCountForSlot) {
            updateRefreshDebugState({
                lastSkipReason: "max_refresh_count_reached",
                currentRefreshCount: refreshState.currentRefreshCount,
                maxRefreshCount: maxRefreshCountForSlot
            });
            adsDebugLog("[ads-refresh]", divId, "skip refresh: max refresh count reached", {
                currentRefreshCount: refreshState.currentRefreshCount,
                maxRefreshCount: maxRefreshCountForSlot
            });
            return;
        }

        if (refreshState.aboveThresholdSinceTimestamp === null) {
            refreshState.aboveThresholdSinceTimestamp = Date.now();
            updateRefreshDebugState({ aboveThresholdSinceTimestamp: refreshState.aboveThresholdSinceTimestamp });
        }

        // The interval depends on which advertiser is currently rendered in this slot --
        // see GAM_SLOW_REFRESH_ADVERTISER_IDS above. Read it fresh on every scheduling
        // pass, since a different advertiser can win on each refresh.
        var refreshIntervalSeconds = getRefreshIntervalSecondsForSlot(slotAd);

        refreshState.scheduledRefreshTimeoutId = setTimeout(function () {
            refreshState.scheduledRefreshTimeoutId = null;
            updateRefreshDebugState({ scheduledRefreshTimeoutId: refreshState.scheduledRefreshTimeoutId });

            // If the tab is hidden, we reset timing and wait for visibility to return.
            if (document.visibilityState !== "visible") {
                refreshState.aboveThresholdSinceTimestamp = null;
                updateRefreshDebugState({
                    aboveThresholdSinceTimestamp: refreshState.aboveThresholdSinceTimestamp,
                    lastSkipReason: "tab_hidden_on_window_end"
                });
                return;
            }

            // Must still be above the threshold when the window completes.
            if (!refreshState.isAboveThreshold) {
                refreshState.aboveThresholdSinceTimestamp = null;
                updateRefreshDebugState({
                    aboveThresholdSinceTimestamp: refreshState.aboveThresholdSinceTimestamp,
                    lastSkipReason: "below_threshold_on_window_end"
                });
                return;
            }

            attemptSlotRefresh();

            // Continue refreshing on the same cadence while the slot remains above threshold.
            refreshState.aboveThresholdSinceTimestamp = Date.now();
            updateRefreshDebugState({ aboveThresholdSinceTimestamp: refreshState.aboveThresholdSinceTimestamp });

            scheduleRefreshWindowIfEligible();
        }, refreshIntervalSeconds * 1000);

        updateRefreshDebugState({
            scheduledRefreshTimeoutId: refreshState.scheduledRefreshTimeoutId,
            lastEvent: "scheduled_refresh_window",
            secondsToWait: refreshIntervalSeconds,
            lastIntersectionRatio: refreshState.lastIntersectionRatio
        });

        adsDebugLog(
            "[ads-refresh]",
            divId,
            "schedule refresh window",
            { seconds: refreshIntervalSeconds, ratio: refreshState.lastIntersectionRatio }
        );
    }

    function attemptSlotRefresh() {
        if (maxRefreshCountForSlot !== null && refreshState.currentRefreshCount >= maxRefreshCountForSlot) {
            updateRefreshDebugState({
                lastSkipReason: "max_refresh_count_reached",
                currentRefreshCount: refreshState.currentRefreshCount,
                maxRefreshCount: maxRefreshCountForSlot
            });
            adsDebugLog("[ads-refresh]", divId, "skip refresh: max refresh count reached", {
                currentRefreshCount: refreshState.currentRefreshCount,
                maxRefreshCount: maxRefreshCountForSlot
            });
            return;
        }

        var advertiserID = getCurrentAdvertiserIdForSlot(slotAd);

        if (GAM_NOREFRESH_ADVERTISER_IDS.includes(advertiserID)) {
            updateRefreshDebugState({
                lastSkipReason: "no_refresh_advertiser",
                lastAdvertiserId: advertiserID
            });
            adsDebugLog("[ads-refresh]", divId, "skip refresh: advertiser blocked", { advertiserId: advertiserID });
            return;
        }

        if (GAM_NOREFRESH_ADUNITS.includes(divId)) {
            updateRefreshDebugState({ lastSkipReason: "no_refresh_adunit" });
            adsDebugLog("[ads-refresh]", divId, "skip refresh: adunit blocked");
            return;
        }

        updateRefreshDebugState({
            lastEvent: "attempt_refresh",
            lastAttemptTimestamp: Date.now(),
            lastAdvertiserId: advertiserID
        });

        adsDebugLog("[ads-refresh]", divId, "attempt refresh");

        // Run a Prebid auction before refreshing the GPT slot.
        pbjs.que.push(function () {
            pbjs.requestBids({
                labels: deviceLabels,
                timeout: getPrebidTimeoutForSlot(divId),
                adUnitCodes: [divId],
                bidsBackHandler: function (bidsResponse, timedOut) {
                    applyAuctionQualityTargeting(
                        divId,
                        slotAd,
                        bidsResponse,
                        timedOut
                    );
                    pbjs.setTargetingForGPTAsync([divId]);

                    googletag.cmd.push(function () {
                        refreshState.currentRefreshCount += 1;
                        var refreshBucket = refreshState.currentRefreshCount <= 3 ? 'r1_3' : 'r4_plus';

                        slotAd.setTargeting('refresh_count', String(refreshState.currentRefreshCount));
                        slotAd.setTargeting('refresh_bucket', refreshBucket);

                        updateRefreshDebugState({
                            lastEvent: "gpt_refresh_called",
                            lastRefreshRequestTimestamp: Date.now(),
                            currentRefreshCount: refreshState.currentRefreshCount,
                            refreshBucket: refreshBucket,
                            maxRefreshCount: maxRefreshCountForSlot
                        });

                        adsDebugLog("[ads-refresh]", divId, "REFRESH", {
                            refreshCount: refreshState.currentRefreshCount,
                            refreshBucket: refreshBucket,
                            maxRefreshCount: maxRefreshCountForSlot
                        });

                        // Telemetry (25092026): only upgrades a pending consent-wait value.
                        recordFirstRequestTelemetryBeforeRefresh('refresh:' + divId);
                        googletag.pubads().refresh([slotAd]);
                    });
                }
            });
        });
    }

    // Reset timing when the tab becomes hidden; reschedule on return if the slot is still above threshold.
    document.addEventListener("visibilitychange", function () {
        if (document.visibilityState !== "visible") {
            refreshState.aboveThresholdSinceTimestamp = null;
            clearScheduledRefreshWindow();
            return;
        }

        if (refreshState.isAboveThreshold) {
            refreshState.aboveThresholdSinceTimestamp = Date.now();
            scheduleRefreshWindowIfEligible();
        }
    });

    // Observe viewability ratio, not just "intersecting".
    var viewabilityObserver = new IntersectionObserver(
        function (viewabilityEntries) {
            viewabilityEntries.forEach(function (viewabilityEntry) {
                if (!viewabilityEntry || !viewabilityEntry.target) {
                    return;
                }

                // Only handle the slot elements for this GPT slot.
                if (viewabilityEntry.target.id !== divId) {
                    return;
                }

                refreshState.lastIntersectionRatio = viewabilityEntry.intersectionRatio || 0;
                updateRefreshDebugState({ lastIntersectionRatio: refreshState.lastIntersectionRatio });

                var isAboveThresholdNow = refreshState.lastIntersectionRatio >= VISIBILITY_PERCENT_FOR_REFRESH;

                if (isAboveThresholdNow && !refreshState.isAboveThreshold) {
                    refreshState.isAboveThreshold = true;
                    refreshState.aboveThresholdSinceTimestamp = Date.now();

                    updateRefreshDebugState({
                        isAboveThreshold: refreshState.isAboveThreshold,
                        aboveThresholdSinceTimestamp: refreshState.aboveThresholdSinceTimestamp,
                        lastIntersectionRatio: refreshState.lastIntersectionRatio,
                        lastEvent: "threshold_enter"
                    });

                    adsDebugLog(
                        "[ads-refresh]",
                        divId,
                        "threshold enter",
                        { ratio: refreshState.lastIntersectionRatio, threshold: VISIBILITY_PERCENT_FOR_REFRESH }
                    );

                    scheduleRefreshWindowIfEligible();
                    return;
                }

                if (!isAboveThresholdNow && refreshState.isAboveThreshold) {
                    refreshState.isAboveThreshold = false;
                    refreshState.aboveThresholdSinceTimestamp = null;

                    updateRefreshDebugState({
                        isAboveThreshold: refreshState.isAboveThreshold,
                        aboveThresholdSinceTimestamp: refreshState.aboveThresholdSinceTimestamp,
                        lastIntersectionRatio: refreshState.lastIntersectionRatio,
                        lastEvent: "threshold_exit"
                    });

                    adsDebugLog(
                        "[ads-refresh]",
                        divId,
                        "threshold exit",
                        { ratio: refreshState.lastIntersectionRatio, threshold: VISIBILITY_PERCENT_FOR_REFRESH }
                    );

                    clearScheduledRefreshWindow();
                }
            });
        },
        {
            threshold: [0, VISIBILITY_PERCENT_FOR_REFRESH, 1]
        }
    );

    document.querySelectorAll("#" + divId).forEach(function (viewabilitySlotElement) {
        viewabilityObserver.observe(viewabilitySlotElement);
    });
    });
    });

}


// =============================================================================
// 01102026-2 -- ASYNC-SAFE BOOT (kept at the very end of the file on purpose:
// every `var` above has been assigned by now, which is not true at the top of the
// file where the DOMContentLoaded / load listeners are registered).
// =============================================================================
//
// The PRODUCTION template loads this file `async` from 01.10.2026 (it was `defer`
// before, which guaranteed execution before DOMContentLoaded). An async script can
// execute after DOMContentLoaded -- or even after load -- has already fired, and
// then no event is coming for the listeners at the top of the file: boot
// immediately instead of never. While the document is still parsing, the
// DOMContentLoaded listener boots the wrapper exactly as before.
if (document.readyState !== 'loading' && !window.prebidAuctionRun) {
    window.prebidAuctionRun = true;
    recordFirstRequestMilestone('wrapperStartMs', 'wrapperStartReason', 'readyState:' + document.readyState); // telemetry
    runWrapper();
}
